<template>
  <div class="relative w-full flex flex-col overflow-hidden" ref="viewer_page" style="height: 100vh;">
      <Subheader
        :items="subheaderItems"
        :context="subheaderContext"
        @action="_OnSubheaderAction"
      />
      <div class="relative w-full flex flex-1 min-h-0">

        <!-- Estado inicial (sin archivo) — dropzone SOLO en design mode. En
             progress mode mostramos un loader mientras Entrega B inyecta la
             geometría desde el .epcb. -->
        <div v-if="!hasFile && viewerMode === 'design'" class="flex-1 flex p-4">
          <b-upload
            class="upload-full"
            :value="uploadedFile"
            drag-drop
            accept=".dxf,.dwg"
            @input="_OnFileSelected">
            <div class="has-text-centered">
              <p><b-icon icon="upload" size="is-large" pack="fas"></b-icon></p>
              <p>{{ $t('drop_files') }}</p>
            </div>
          </b-upload>
        </div>

        <!-- Placeholder en progress mode mientras se descarga / decodifica
             el .epcb. Una vez Entrega B inyecte la escena, hasFile pasa a
             true y este bloque deja de mostrarse. -->
        <div v-else-if="!hasFile && viewerMode === 'progress'" class="flex-1 flex items-center justify-center p-8">
          <div class="text-center">
            <b-icon pack="fas" icon="spinner" custom-class="fa-spin" size="is-large"></b-icon>
            <p class="mt-2 text-sm text-gray-500">{{ $t('loading_design') }}…</p>
          </div>
        </div>

        <!-- Estado normal: viewer renderizando el cadJson (procesado si existe, sino el original) -->
        <template v-else>
          <div class="relative w-full flex flex-1 min-h-0">
            <Viewer
              :cad-json="processedCadJson || cadJson"
              :fonts="fonts"
              ref="viewer"
              @epc-loaded="onSceneLoaded"
              @epc-pointerdown="onClick"
              @epc-error="onError"
            />
          </div>
          <ViewerPanelScreenshot v-if="showPanelScreenshot"
            ref="refScreenshot"
            :screenshot="screenshot"
            @epc-close="(e) => { showPanelScreenshot = !e }"/>

          <!-- Overlay interactivo: solo no-mechanical. Genera hitboxes por
               segmento de polilínea para capturar clicks sin tocar el render
               principal del Viewer.
               Pasamos `$refs.viewer` directo (no a través de data) para evitar
               que Vue intente observar reactivamente el árbol THREE.js entero. -->
          <DxfInteractiveOverlay
            v-if="advanceOverlayEnabled && viewerReady"
            ref="refDxfOverlay"
            :viewer="$refs.viewer"
            :cad-json="processedCadJson || cadJson"
            :filter="_AdvanceOverlayFilter"
            @epc-entity-click="_OnEntityClickForInfo"
            @epc-entity-dblclick="_OnEntityClickForAdvance"
          />
        </template>

      </div>

      <!-- Input invisible que dispara el botón "Subir archivo" del header -->
      <input
        ref="fileInputHidden"
        type="file"
        accept=".dxf,.dwg"
        class="hidden"
        @change="_OnHiddenFileInputChange"
      />

      <!-- Modal de selección de capas para electrical/trenches. Se abre al
           pulsar el botón "Procesar" del Toolbar. Al confirmar (apply) emite
           las capas seleccionadas (con nombre editable) y encadenamos con
           SavePictogramModal — mismo patrón que "Configurar → Guardar" del
           mecánico. La lista de capas se pasa por `show(layers)`, no por prop. -->
      <SelectLayersModal
        ref="refSelectLayersModal"
        @epc-apply="_OnSelectLayersApply"
      />

      <!-- Indicador flotante de la capa activa (solo progress mode). Le dice
           al usuario en qué capa está registrando avance y, al pulsarlo,
           despliega el panel de Capas para cambiarla. -->
      <div
        v-if="isProgressMode && hasFile"
        class="active-layer-pill"
        :title="$t('change_layer')"
        @click="showLayerPanel = !showLayerPanel"
      >
        <b-icon pack="fas" icon="layer-group" size="is-small" />
        <span class="active-layer-pill__text">
          <template v-if="activeLayerCode">{{ $t('you_are_on_layer') }}: <strong>{{ activeLayerName }}</strong></template>
          <template v-else>{{ $t('all_layers') }}</template>
        </span>
        <b-icon pack="fas" icon="chevron-down" size="is-small" />
      </div>

      <Toolbar
        v-if="hasFile"
        :buttons="toolbarButtons"
        :context="toolbarContext"
        variant="inline"
        @action="_OnToolbarAction"
      />

      <LayersPanel
        :visible="showLayerPanel"
        :layers="filterLayers"
        :active-name="activeLayerCode"
        :locked="!isProgressMode || !progressViewerConfig.allowLayerSwitch"
        @toggleLayer="_OnToggleLayer"
        @toggleAll="_OnToggleAll"
        @activate-layer="_OnActivateLayer"
        @epc-close="(close) => showLayerPanel = !close"
      />

      <ViewerPanelViews
        :visible="showPanelViews"
        :views="viewsList"
        :active-view="displayView"
        @view-selected="_OnViewSelected"
        @epc-close="showPanelViews = false"
      />

      <ConfigureSubdivisionsModal
        ref="refConfigureSubdivisionsModal"
        @epc-apply="_OnConfigureSubdivisionsApply" />

      <SavePictogramModal
        ref="refSavePictogramModal"
        @epc-apply="_OnSavePictogramApply" />

      <SaveSuccessModal
        ref="refSaveSuccessModal"
        @epc-close="_OnSaveSuccessClose"
        @epc-new="_OnSaveSuccessNew"
        @epc-back="_OnSaveSuccessBack" />

      <ViewerSpinnerLoader ref="refSpinnerLoader" :is-loading="isLoading" :custom-loader-text="customLoaderText"/>

      <!-- Modal de avance lineal — se abre al clickear una polilínea con ancho
           en viewerMode='progress'. RegisterAdvanceModal mantiene el estado
           UI internamente; al Aplicar emite epc-apply con { direction, meters,
           status } y el LayoutBuilder los procesa en _OnAdvanceApply. -->
      <RegisterAdvanceModal
        ref="refRegisterAdvanceModal"
        @epc-apply="_OnAdvanceApply" />

      <!-- Popover de INFO (un click en el visor): campos extra + certificación
           por metros + progreso. El doble click abre el modal de avance. -->
      <ElectricalInfoPopover
        v-if="infoPopover.visible"
        :position="infoPopover.position"
        :data="infoPopover.data"
        :loading="infoPopover.loading"
        :segment-points="infoPopover.segmentPoints"
        :anchor="infoPopover.anchor"
        @close="_CloseInfoPopover" />

      <!-- Tooltip flotante con el detalle del avance al pasar el mouse por
           una polilínea ya pintada. Position fixed contra el viewport — el
           handler de pointermove pone clientX/Y + offset. -->
      <div
        v-if="advanceTooltip.visible"
        class="advance-tooltip"
        :style="{ left: advanceTooltip.x + 'px', top: advanceTooltip.y + 'px' }"
      >
        <div class="advance-tooltip__title">{{ $t('linear_advance') }}</div>
        <div>{{ $t('total_length') }}: {{ advanceTooltip.totalLength.toFixed(2) }} m</div>
        <div v-if="advanceTooltip.breakdown.length === 0" class="text-white/60 italic mt-0.5">
          {{ $t('no_progress_registered') }}
        </div>
        <div v-for="brk in advanceTooltip.breakdown" :key="'tooltip-brk-' + brk.value"
             class="flex items-center gap-1 mt-0.5">
          <span class="inline-block w-3 h-3 rounded-sm border border-white/30"
                :style="{ backgroundColor: brk.color }"></span>
          <span>{{ brk.label || '—' }}: {{ brk.meters.toFixed(2) }} m</span>
        </div>
      </div>
    </div>
</template>

<script>
import Viewer from "../viewer/Viewer"
// imports modales
import ConfigureSubdivisionsModal from "../viewer/components/modals/ConfigureSubdivisionsModal.vue"
import SavePictogramModal from "../viewer/components/modals/SavePictogramModal.vue"
import SaveSuccessModal from "../viewer/components/modals/SaveSuccessModal.vue"
// imports componentes
// servicios y helpers de guardado
import { savePictogram, uploadNewPictogramVersion } from "./services/pictogram.service"
import { processSubdivisions } from "./services/process-subdivisions.service"
import { captureViewerByViews, captureViewerSnapshot } from "./helpers/screenshot.helper"

import Vue from "vue"
import { FONTS } from "./constants/fonts"
import LayersPanel from "../shared/components/panels/LayersPanel.vue"
import Subheader from "../shared/Subheader.vue"
import ViewerPanelViews from "../shared/components/panels/ViewerPanelViews.vue"
import ViewerSpinnerLoader from "../viewer-konva/ViewerSpinnerLoader.vue"
import ViewerPanelScreenshot from "../viewer-konva/ViewerPanelScreenshot.vue"

import { createLogger } from "../utils/logger"
const logger = createLogger('LayoutBuilder')

import Toolbar from "../shared/Toolbar.vue"

import { getLayerDescriptors } from "./helpers/layer-descriptors.helper"
import { getTextLayers, layerBelongsToView } from "./helpers/view-filter.helper"
import {
  buildToolbarButtons, buildToolbarContext,
  buildSubheaderItems, buildSubheaderContext,
} from "./helpers/menu-schemas.helper"
import { VIEW_CODE, DEFAULT_VIEW } from "./constants/view-code"
import { isFullScreenActive, requestFullScreen, exitFullScreen } from "../shared/helpers/ui.helpers"
import { parseCadFile } from "./services/cad-file.service"
import { PICTOGRAM_TYPES } from "../constants/pictogram-types"
import DxfInteractiveOverlay from "../components/DxfInteractiveOverlay.vue"
import * as THREE from "three"
import { computeLayerMetrics } from "./helpers/dxf-layer-metrics.helper"
import { encodeScene } from "./helpers/binary-scene.encoder"
import { decodeScene } from "./helpers/binary-scene.decoder"
import { registerLayerProgress, getPictogramComponents } from "../services/pictograms.service"
import { uploadFile } from "../shared/services/uppy-client.service"
import SelectLayersModal from "./components/modals/SelectLayersModal.vue"
import RegisterAdvanceModal from "./components/modals/RegisterAdvanceModal.vue"
import ElectricalInfoPopover from "./components/ElectricalInfoPopover.vue"
import { getElectricalComponentInfo } from "../api/pictograms.api.js"

// Estados posibles del avance lineal — color es hex Three.js, no CSS.
// El orden determina el que se muestra primero en el selector.
const ADVANCE_STATUSES = [
  { value: 'pending',     color: 0xfdcb6e }, // amarillo
  { value: 'in_progress', color: 0x00b894 }, // verde (default)
  { value: 'blocked',     color: 0xe17055 }, // rojo
  { value: 'completed',   color: 0x0984e3 }, // azul
]
const ADVANCE_DEFAULT_STATUS = 'in_progress'
const ADVANCE_STORAGE_PREFIX = 'epc.layout-builder.advance.v1.'

// Colores uniformes del resaltado del footer (dropdown "Resaltar"). Un color
// para los elementos SIN avance registrado (pendientes) y otro para los que
// tienen avance. Son hex Three.js (int), no CSS.
const HIGHLIGHT_COLORS = {
  without: 0xff2d2d, // rojo chillante — pendientes (sin avance)
  with:    0x1a8a1c, // verde oscuro — con avance
}

// El resaltado se pinta un poco más ancho que la polilínea original para que se
// note más (solo afecta al ribbon del resaltado, no al avance ni al diseño).
const HIGHLIGHT_WIDTH_FACTOR = 1.4

// Atenuado de capas NO-activas (progress mode): en vez de bajar opacidad,
// las pintamos de un gris plano y neutro (sin color) para que la capa activa
// resalte. `LAYER` es el gris de la geometría (0..1 por canal); `ADVANCE_HEX`
// + `ADVANCE_OPACITY` es el gris —más tenue— de los carriles de avance.
const DIM_LAYER_GRAY      = 0.72
const DIM_ADVANCE_GRAY_HEX = 0xb0b0b0
const DIM_ADVANCE_OPACITY  = 0.5

export default {
  name: "LayoutBuilder",
  components: {
    Viewer,

    ConfigureSubdivisionsModal,
    SavePictogramModal,
    SaveSuccessModal,

    LayersPanel,
    ViewerPanelViews,
    ViewerSpinnerLoader,
    ViewerPanelScreenshot,

    Subheader,
    Toolbar,

    DxfInteractiveOverlay,
    SelectLayersModal,
    RegisterAdvanceModal,
    ElectricalInfoPopover,
  },

  props: {
    pictogram: {
      type: Object,
      default() { return {} },
    },
    typePictogram: { type: String, default: 'mechanical_installation' }, // si es null es pictograma de parque fotovoltaico sino de evacuacion line
    workLayer: { type: String, default: null },
    canActionsPictogram: { type:Object, default() { return {} } }, // acciones que se puede realizar sobre el pictograma por defecto ninguna
    entityId: { type: [Number, String], required: true },
    entityType: { type: String, default: 'project' },
    /**
     * Modo de operación:
     *   - 'create' (default): flujo normal — al guardar abre SavePictogramModal y crea pictograma nuevo
     *   - 'upload-new-version': flujo de actualizar — al guardar dispara upload + redirect a /process con query
     */
     mode: {
      type: String,
      default: 'create',
      validator: (v) => ['create', 'upload-new-version'].includes(v),
    },

    /**
     * Eje ortogonal a `mode`: qué FAMILIA de features expone el visor.
     *   - 'design'  (default): admin carga/procesa/guarda el diseño. NO muestra
     *     overlay de avance ni tooltip ni modal de avance lineal.
     *   - 'progress': lado cliente — el usuario registra avance sobre las
     *     polilíneas con width. Activa DxfInteractiveOverlay, modal de avance,
     *     hover tooltip y rehidratado desde localStorage.
     * Ambas familias se pueden seguir desarrollando en paralelo sin romper
     * el modo opuesto — todo el código de avance se conserva intacto y solo
     * se gatea por este flag.
     */
    viewerMode: {
      type: String,
      default: 'design',
      validator: (v) => ['design', 'progress'].includes(v),
    },
  },

  data() {
    return {
      filterLayers: [],
      showLayerPanel: false,
      isDraggable: true,

      showPanelViews: false,
      displayView: DEFAULT_VIEW,   // código de la vista activa (pilings|structures|modules|panels). 'structures' es el default.
      showPanelScreenshot: false, // bandera que indica si se muestra o no el panel de screenshots

      screenshot: null, // base64 preview del último PNG capturado


      isLoading: false, // Indica si esta visible el loader
      customLoaderText: null, // texto mostrado en el loader

      // Archivo CAD cargado por el usuario. Mientras es null, la vista
      // muestra el dropzone; cuando se setea, se renderiza el viewer.
      uploadedFile: null,
      cadJson: null,           // resultado del parseo
      isParsing: false,
      parseError: null,

      // Configuración aplicada desde ConfigureSubdivisionsModal (la usará D4 para procesar)
      cadLayeredDesign: null,
      createElements: { pilings: false, modules: false },

      processedCadJson: null,        // cadJson tras pasar por el worker (con subdivisiones)
      totalEntitiesDesign: { pilings: 0, modules: 0, panels: 0 },       // totales devueltos por el worker

      // Avance lineal (solo no-mechanical). Flag que indica que el Viewer
      // ya está montado y su DxfViewer/escena Three.js están disponibles —
      // en cuanto pasa a true, el template pasa `$refs.viewer` al overlay.
      //
      // IMPORTANTE: NO guardamos `$refs.viewer` ni el cad entity (que tiene
      // vertices) en data(). Vue 2 los observaría recursivamente y para un DXF
      // grande eso cuelga el thread durante segundos. Lo que necesitamos no
      // reactivo se inicializa en created() como propiedad de instancia.
      viewerReady: false,
      // El estado del modal de avance lineal (direction/meters/status) vive
      // dentro de RegisterAdvanceModal.vue. Acá solo guardamos el resultado
      // tras el apply, vía _OnAdvanceApply → _advanceState (instance prop).
      //
      // Tooltip mostrado al hacer hover sobre una polilínea con avance pintado.
      // Se posiciona con position:fixed usando coords del mouse (clientX/Y).
      advanceTooltip: {
        visible:     false,
        x:           0,
        y:           0,
        totalMeters: 0,
        totalLength: 0,
        // Acumulado por status, derivado de _advanceState[handle].segments.
        // Cada entry: { value, label, color, meters }.
        breakdown:   [],
      },
      // Popover de INFO (un click): extra_attributes + certificación + progreso.
      // El doble click abre el modal de avance (RegisterAdvanceModal).
      infoPopover: {
        visible:  false,
        position: { x: 0, y: 0 },
        data:     null,
        loading:  false,
        // Contorno del segmento proyectado a pantalla + punto ancla del leader
        // (se recalculan por rAF mientras el popover está abierto → siguen al
        // segmento en pan/zoom).
        segmentPoints: [],
        anchor:        null,
      },
      advanceStatuses: ADVANCE_STATUSES.map(s => s.value),

      // Capas para el SelectLayersModal (solo no-mechanical). Se rellena lazy
      // al abrir el modal (`_BuildLayersList`). Por defecto todas las capas
      // arrancan seleccionadas — el usuario destildara las que no quiera
      // persistir en pictogram_layers.
      dxfLayersInfo:      [], // [{name, displayName, color, entityCount, lengthMeters}]
      selectedLayerCodes: [], // [string] subset de dxfLayersInfo[].name
      // Nombres editados por el usuario en SelectLayersModal: { [originalName]: customName }.
      // Sólo presentes las capas renombradas — el resto cae a displayName del DxfViewer.
      customLayerNames:   {},
      // Capa activa en el panel "Capas" (solo cliente / progress mode).
      // Si tiene un code (electrical_n / trench_n), únicamente las
      // polilíneas de esa capa responden a click. Si es null, todas las
      // capas trackeables son clickeables (comportamiento default).
      activeLayerCode:    null,
      // Modo de resaltado activo del dropdown "Resaltar" del footer:
      //   null      → sin resaltado
      //   'without' → resaltados los elementos SIN avance registrado
      //   'with'    → resaltados los elementos CON avance registrado
      highlightMode:      null,
      // Modo de visualización de estados sobre cada elemento:
      //   'last'         → solo el último estado registrado (default)
      //   'all'          → todos los estados (carriles paralelos)
      //   <statusValue>  → solo ese estado puntual
      statusViewMode:     'last',
    }
  },

  computed: {
    /** True cuando el usuario ya cargó un archivo CAD. Controla la vista inicial vs viewer. */
    hasFile() {
      return !!this.cadJson
    },

    /** True cuando el worker ya generó las subdivisiones (modal aceptado en D4). */
    hasProcessed() {
      return !!this.processedCadJson
    },

    /**
     * Lista de vistas para el panel "Vistas".
     * Solo tiene sentido tras procesar — antes está vacía y el botón del subheader está disabled.
     * "Estructuras" es la vista por defecto al procesar; no hay vista "Todas".
     * "Paneles" se excluye porque son demasiado detallados para usarse como vista filtrada.
     */
    viewsList() {
      if (!this.hasProcessed) return []
      return this._GetLayers()
        .filter(l => l.code !== 'panels')
        .map(l => ({
          code: l.code,
          name: this.$t(l.code),
          icon: l.icon,
        }))
    },

    /**
     * Set de nombres de layers que contienen TEXT o MTEXT.
     * Escanea tres lugares porque los textos en DXF pueden vivir en:
     *   1. Top-level entities (caso clásico)
     *   2. Dentro de blocks (definiciones reusables) — sus entities tienen su propio layer
     *   3. Layers de los INSERT que referencian esos blocks (porque al instanciar el block,
     *      el INSERT aporta su layer y las entities con layer="0" lo heredan)
     */
    textLayers() {
      return getTextLayers(this.processedCadJson || this.cadJson)
    },

    /**
     * Total de estructuras detectadas en el DXF cargado (POLYLINE/LWPOLYLINE).
     * Lo usa _GetLayers() para el descriptor del layer "structures" que se pasa al worker.
     */
    total_structures() {
      return (this.cadJson?.entities || []).filter(
        e => e.type === 'POLYLINE' || e.type === 'LWPOLYLINE'
      ).length
    },

    /** Schema declarativo del Toolbar (delega al helper menu-schemas). */
    toolbarButtons() {
      return buildToolbarButtons()
    },

    /** Estado del Toolbar para resolver active/visible/disabled de cada item. */
    toolbarContext() {
      return buildToolbarContext({
        pictogram:      this.pictogram,
        showLayerPanel: this.showLayerPanel,
        isDraggable:    this.isDraggable,
        allowSave:      this._AllowedAction('save'),
        typePictogram:  this.typePictogram,
        viewerMode:     this.viewerMode,
        features:       {
          showHighlightMenu: this.progressViewerConfig.showHighlightMenu,
          showStatusFilter:  this.progressViewerConfig.showStatusFilter,
        },
        highlightMode:       this.highlightMode,
        statusViewMode:      this.statusViewMode,
        activeLayerStatuses: this.activeLayerStatuses,
      })
    },

    /**
     * Estados de la capa activa (para las opciones dinámicas del dropdown
     * "Ver avance"). Formato uniforme { value, label, color } vía
     * _GetStatusesForLayer. Si no hay capa activa, no ofrecemos estados
     * puntuales (solo "último" y "todos").
     */
    activeLayerStatuses() {
      const code = this.activeLayerCode
      if (!code) return []
      const pl = (this.pictogram && this.pictogram.layers || []).find(l => l.code === code)
      if (!pl) return []
      return this._GetStatusesForLayer(pl)
    },

    /**
     * Configuración del visor de avance (progress mode), pensada para ser
     * editable por el administrador desde `pictogram.metadata.progress_viewer`.
     * Ambas features vienen ACTIVAS por defecto; el admin las desactiva
     * escribiendo `false` en la clave correspondiente. Se deja preparado el
     * canal (metadata) — cuando exista la UI de admin solo hay que persistir
     * esas claves; el frontend ya las respeta.
     *
     *   - allowLayerSwitch:  permite cambiar de capa activa dentro del mismo
     *     modo de registro (panel de Capas desbloqueado).
     *   - showHighlightMenu: muestra el dropdown "Resaltar" en el footer.
     *   - showStatusFilter:  muestra el dropdown "Ver avance" (qué estados se
     *     visualizan sobre cada elemento) en el footer.
     */
    progressViewerConfig() {
      const cfg = (this.pictogram && this.pictogram.metadata && this.pictogram.metadata.progress_viewer) || {}
      return {
        allowLayerSwitch:  cfg.allow_layer_switch  !== false,
        showHighlightMenu: cfg.show_highlight_menu !== false,
        showStatusFilter:  cfg.show_status_filter  !== false,
      }
    },

    /** Schema declarativo del Subheader (cambia según haya o no archivo). */
    subheaderItems() {
      return buildSubheaderItems({ hasFile: this.hasFile })
    },

    /** Estado del Subheader para resolver active/disabled de cada item. */
    subheaderContext() {
      return buildSubheaderContext({
        hasProcessed:   this.hasProcessed,
        showPanelViews: this.showPanelViews,
        showLayerPanel: this.showLayerPanel,
        viewerMode:     this.viewerMode,
      })
    },

    /** True cuando el LayoutBuilder se usa como visor de avance del cliente. */
    isProgressMode() {
      return this.viewerMode === 'progress'
    },

    /**
     * Nombre humano de la capa activa (para el indicador flotante). Resuelve
     * el `name` del pictogram_layer por su code; si no hay capa activa,
     * devuelve el rótulo "todas las capas".
     */
    activeLayerName() {
      const code = this.activeLayerCode
      if (!code) return this.$t('all_layers')
      const pl = (this.pictogram && this.pictogram.layers || []).find(l => l.code === code)
      return (pl && pl.name) || code
    },

    /**
     * El overlay interactivo para pintar avance lineal aplica solo en:
     *   - tipos no-mechanical (electrical_installation, electrical_trenches, …)
     *   - viewerMode === 'progress' (lado cliente)
     * En 'design' (admin) está deshabilitado — el admin solo carga el diseño,
     * el registro de avance vive en el viewer cliente.
     */
    advanceOverlayEnabled() {
      return this.hasFile
        && this.typePictogram !== PICTOGRAM_TYPES.MECHANICAL
        && this.isProgressMode
    },

    /**
     * Lista que recibe el SelectLayersModal. Combina la info estructural
     * (dxfLayersInfo) con el flag `selected` derivado de selectedLayerCodes
     * para mantener una sola fuente de verdad sobre la selección.
     */
    dxfLayersForModal() {
      const sel = new Set(this.selectedLayerCodes)
      return this.dxfLayersInfo.map(l => ({
        ...l,
        selected: sel.has(l.name),
      }))
    },
  },

  watch: {
    showLayerPanel(open) {
      if (open) this._RefreshFilterLayers()
    },
    // Si el usuario cambia de vista mientras el panel de capas está abierto,
    // refrescamos la lista para que solo aparezcan las capas relevantes a la vista nueva.
    displayView() {
      if (this.showLayerPanel) this._RefreshFilterLayers()
    },
    /**
     * Si el padre (typicamente el dispatcher de webviewer) pasa un workLayer
     * por la URL — ej. `/pictogram/X/trench_3` — fijamos esa capa como
     * activa INICIAL. Solo polilíneas de esa capa van a permitir registrar
     * avance, y las demás capas (junto con sus carriles de avance pintados)
     * quedan atenuadas. En progress mode el panel de Capas NO se bloquea:
     * el usuario puede cambiar de capa activa sin salir del visor (ver
     * _OnActivateLayer), y el foco visual + el filtro de clicks se recalculan
     * sobre la nueva capa. Si workLayer cambia o vuelve a null, actualizamos.
     */
    workLayer: {
      immediate: true,
      handler(v) {
        if (v) {
          this.activeLayerCode = v
          // El dim se aplica de verdad cuando ya hay viewer + scene cargada;
          // si todavía no, _ApplyLayerDim es no-op y onSceneLoaded lo
          // disparará tras cargar.
          this._ApplyLayerDim()
        }
      },
    },
  },

  mounted() {

  },

  methods: {
    /**
     * Handler único de @action emitido por el Toolbar.
     * Despacha por `handler` y delega a los métodos existentes.
     * Las llamadas a $refs.viewer están guardadas porque el viewer
     * puede estar comentado durante la validación.
     */
    _OnToolbarAction({ handler, payload }) {
      switch (handler) {
        case 'toggle-layers-panel':
          this.showLayerPanel = !this.showLayerPanel
          break
        case 'pan':
          this.isDraggable = payload
          if (this.$refs.viewer) this.$refs.viewer.EnableDraggable(this.isDraggable)
          break
        case 'fit':        this._OnFitContent(); break
        case 'zoom-in':    this._OnClickZoom(true); break
        case 'zoom-out':   this._OnClickZoom(false); break
        case 'fullscreen': this._OnClickFullScreen(); break
        case 'configure':  this._HandleConfigureSubdivisions(); break
        case 'reset':      this._HandleResetViewer(); break
        case 'save':       this._HandleClickSave(); break
        case 'process':    this._HandleClickProcess(); break
        case 'highlight':  this._OnHighlightSelected(payload); break
        case 'status-view': this._OnStatusViewSelected(payload); break
      }
    },

    /**
     * Botón "Procesar" en no-mechanical: abre el modal de selección de capas.
     * Construye la lista lazy (en el momento de abrir, no en onSceneLoaded) para
     * evitar cómputo innecesario si el usuario nunca llega a procesar.
     * Si ya editó nombres en una apertura anterior, los respetamos pasando los
     * `displayName` actuales mediante `customLayerNames`.
     */
    _HandleClickProcess() {
      if (!this.cadJson) return
      this._BuildLayersList()
      const initial = this.dxfLayersForModal.map(l => ({
        ...l,
        // Si el usuario ya renombró antes, preservamos su edición.
        displayName: this.customLayerNames[l.name] || l.displayName,
      }))
      this.$refs.refSelectLayersModal.show(initial)
    },

    /**
     * Apply del SelectLayersModal: persistimos las capas elegidas (con el nombre
     * que el usuario haya editado) y abrimos SavePictogramModal a continuación.
     * Este es el equivalente al "Configurar → Guardar" del flujo mecánico.
     *
     * @param {Array<{name:string, displayName:string}>} selectedLayers
     */
    _OnSelectLayersApply(selectedLayers) {
      this.selectedLayerCodes = selectedLayers.map(l => l.name)
      // Guardamos los nombres custom indexados por `name` original — los reusamos
      // al construir descriptores y al re-abrir el modal.
      const customNames = {}
      for (const l of selectedLayers) {
        if (l.displayName && l.displayName !== l.name) customNames[l.name] = l.displayName
      }
      this.customLayerNames = customNames
      this.$refs.refSavePictogramModal.show()
    },

    /**
     * Handler único de @action emitido por el Subheader.
     * Reutiliza _HandleOpenPanel que ya existía para gestionar la apertura
     * de los paneles laterales.
     */
    _OnSubheaderAction({ handler, payload }) {
      switch (handler) {
        case 'open-panel-views':  this._HandleOpenPanel('panel-views');  break
        case 'open-panel-layers': this._HandleOpenPanel('panel-layers'); break
        case 'upload-file':
          // Click programático al input invisible. Mismo handler que el dropzone del body.
          this.$refs.fileInputHidden.click()
          break
        case 'back':
          this._HandleReturnClick()
          break
      }
    },

    /**
     * Captura el archivo seleccionado desde el <input type="file"> invisible
     * que se dispara cuando el usuario hace click en "Subir archivo" del header.
     */
    _OnHiddenFileInputChange(event) {
      const file = event.target.files && event.target.files[0]
      if (file) this._OnFileSelected(file)
      // Reseteamos para permitir reseleccionar el mismo archivo después.
      event.target.value = ''
    },

    /**
     * Punto único donde el archivo elegido entra al estado del componente.
     * Lo invocan tanto el dropzone (vía v-model="uploadedFile") como el
     * input invisible del header.
     * TODO: aquí irá la integración con backend cuando esté el endpoint.
     */
    async _OnFileSelected(file) {
      // Si se está cargando un archivo nuevo, descartamos cualquier overlay de
      // avance pintado sobre el archivo anterior (handles distintos, escena nueva).
      this._DisposeAllAdvanceMeshes()
      this._UnbindHoverTooltip()
      this._advanceState = Object.create(null) // se re-hidratará tras el load
      // Selección limpia — la lista la reconstruye _BuildLayersList al abrir el modal.
      this.dxfLayersInfo      = []
      this.selectedLayerCodes = []
      this.customLayerNames   = {}
      this.viewerReady = false

      this.uploadedFile = file
      this.cadJson = null
      this.parseError = null
      this.isParsing = true
      this.ShowLoader({ loading: true, text: this.$t('parsing_cad_file') })

      try {
        this.cadJson = await parseCadFile(file, {
          onProgress: (phase, size, total) => {
            const pct = total ? Math.round((size / total) * 100) : null
            const text = this.$t('parsing_cad_file') + (pct !== null ? ` (${pct}%)` : '')
            this.ShowLoader({ loading: true, text })
          },
        })
      } catch (err) {
        this.parseError = err.message
        this.uploadedFile = null   // vuelve al dropzone
        this.$toast.open({
          message: err.message,
          type: 'is-danger',
          position: 'is-top-right',
        })
      } finally {
        this.isParsing = false
        this.ShowLoader({ loading: false, text: null })
      }
    },

    /**
     * Entrada de scene binario (.epcb) para viewerMode='progress' — el caller
     * (webviewer.vue) descarga el binario de S3 y nos lo pasa como ArrayBuffer.
     *
     * Reconstruimos un `cadJson` sintético con LWPOLYLINE entities por cada
     * polyline decodificada. Setearlo dispara el watcher del Viewer →
     * DxfScene.Build → DxfViewer.LoadScene, igual que cuando llega un DXF
     * parseado. Eso mantiene una sola pipeline de render y reusa overlay
     * de avance, panel de capas, etc.
     *
     * Trade-off vs zero-copy puro: el DxfScene re-procesa el cadJson en main
     * thread. Para esta v1 es aceptable — la ganancia real está en evitar
     * descargar y parsear el DXF original (200MB+). Si después es lento con
     * archivos enormes, se optimiza con LoadScene directo (formato del DxfViewer).
     *
     * @param {ArrayBuffer} buf - .epcb decodificable (validado por decodeScene)
     */
    _LoadBinaryScene(buf) {
      try {
        const decoded = decodeScene(buf)
        logger.info(
          'binary scene decoded:',
          decoded.totalPolys, 'polylines /',
          decoded.totalVerts, 'verts /',
          decoded.layers.length, 'layers'
        )

        // Reset de cualquier estado de un load anterior (mismo patrón que
        // _OnFileSelected). En la práctica progress mode abre una sola vez,
        // pero defensivo no daña.
        this._DisposeAllAdvanceMeshes()
        this._DisposeAllHighlightMeshes()
        this.highlightMode = null
        this._UnbindHoverTooltip()
        this._advanceState = Object.create(null)
        this.viewerReady   = false

        // Reconstrucción del cadJson sintético — formato esperado por DxfScene.
        const entities = []
        let entityId = 1
        for (const layer of decoded.layers) {
          for (const poly of layer.polylines) {
            const verts = new Array(poly.vertCount)
            // poly.vertices es Float32Array interleaveado [x0,y0,x1,y1,...]
            for (let i = 0; i < poly.vertCount; i++) {
              verts[i] = { x: poly.vertices[i * 2], y: poly.vertices[i * 2 + 1] }
            }
            entities.push({
              type:     'LWPOLYLINE',
              layer:    layer.code,
              handle:   String(entityId++),
              vertices: verts,
              width:    poly.width || 0,
              shape:    poly.closed,
            })
          }
        }

        // tables.layer.layers — mapping por nombre con color por capa.
        // color 7 = blanco/negro (default DXF) cuando no hay color explícito.
        const layerEntries = {}
        for (const layer of decoded.layers) {
          layerEntries[layer.code] = {
            name:  layer.code,
            color: layer.color || 7,
          }
        }

        // Setear `cadJson` dispara el watcher del Viewer y carga la escena.
        // `hasFile` pasa a true → el placeholder de "Cargando diseño…" se va.
        this.cadJson = {
          header:   {},
          blocks:   {},
          tables:   { layer: { layers: layerEntries } },
          entities,
        }
      } catch (err) {
        logger.error('_LoadBinaryScene error:', err)
        this.$buefy?.toast?.open?.({
          message:  this.$t('error_loading_scene') || err.message || 'Error cargando escena',
          type:     'is-danger',
          position: 'is-top-right',
        })
      }
    },

    ShowLoader(data) {
      this.isLoading = data.loading;
      this.customLoaderText = data.text
    },

    _OnToggleLayer(layer, newState) {
        layer.isVisible = newState
        this.$refs.viewer.ShowLayer(layer.name, newState)
    },

    _OnToggleAll(newState) {
        if (this.filterLayers) {
            for (const layer of this.filterLayers) {
                if (layer.isVisible !== newState) {
                    this._OnToggleLayer(layer, newState)
                }
            }
        }
    },

    /**
     * Click en el nombre de una capa del LayersPanel — activa/desactiva
     * el filtro de clicks. Cuando hay una activa, solo polilíneas de esa
     * capa permiten registrar avance.
     */
    _OnActivateLayer(code) {
      // La activación de capa solo aplica al cliente registrando avance.
      // En admin (viewerMode='design') el panel "Capas" sirve solo para
      // toggle de visibilidad — no hay concepto de capa "activa".
      if (!this.isProgressMode) return
      this.activeLayerCode = code
      // Al cambiar de capa, los estados disponibles cambian; volvemos al modo
      // "último" para no dejar seleccionado un estado que la nueva capa no
      // tiene (lo que dejaría todo sin pintar). Repintamos con el filtro nuevo.
      if (this.statusViewMode !== 'last' && this.statusViewMode !== 'all') {
        this.statusViewMode = 'last'
      }
      this._ApplyLayerDim()
      this._RepaintAllAdvance()
      // Tras elegir una capa, cerramos el panel: el usuario ya seleccionó y
      // vuelve directo al viewer para registrar avance sobre esa capa.
      this.showLayerPanel = false
    },

    /**
     * Atenúa las capas no-activas en el render para reforzar visualmente
     * que solo la activa es interactiva. Sin capa activa, restaura todas
     * a su color original. Solo aplica en viewerMode='progress'.
     *
     * Las capas no-activas se pintan de un GRIS plano y neutro (no se aclaran
     * hacia blanco): así pierden su color y la capa activa —a color pleno—
     * resalta. También se agrisan los carriles de avance ya pintados de esas
     * capas, para que ni el dibujo ni los registros previos distraigan.
     */
    _ApplyLayerDim() {
      if (!this.isProgressMode) return
      const viewer = this.$refs.viewer
      if (!viewer || typeof viewer.SetLayerGrayscale !== 'function') return
      const layers = viewer.GetLayers(true) || []
      const active = this.activeLayerCode
      // Con el resaltado activo TODA la escena va a gris (la única fuente de
      // color pasa a ser el overlay de resaltado); sin resaltado, solo se
      // agrisan las capas no-activas y la activa mantiene su color.
      const highlightOn = !!this.highlightMode
      for (const l of layers) {
        const gray = highlightOn ? true : !!(active && l.name !== active)
        viewer.SetLayerGrayscale(l.name, gray, DIM_LAYER_GRAY)
      }
      // Los carriles de avance viven en THREE.Groups aparte (no son "capas"
      // del DxfViewer), así que SetLayerGrayscale no los toca — los agrisamos
      // manualmente según la capa a la que pertenece cada grupo.
      this._ApplyAdvanceMeshesDim()
    },

    /**
     * Recorre los carriles de avance pintados (_advanceMeshes) y ajusta su
     * opacidad según la capa activa: los de la capa activa (o todos, si no
     * hay ninguna activa) quedan a opacidad plena; los de otras capas se
     * atenúan. Fuerza un render al final para reflejar el cambio.
     */
    _ApplyAdvanceMeshesDim() {
      const meshes = this._advanceMeshes || {}
      for (const h of Object.keys(meshes)) {
        this._DimAdvanceGroup(meshes[h])
      }
      this.$refs.viewer?.Render?.()
    },

    /**
     * Ajusta los materiales de UN carril de avance según la capa activa: si el
     * grupo es de otra capa, se pinta de gris tenue (pierde el color de estado);
     * si es de la capa activa (o no hay capa activa), restaura su color de
     * estado original y opacidad plena. El color base se guarda por material
     * en userData.baseColorHex al crearlo (_MakeAdvanceMaterial).
     */
    _DimAdvanceGroup(group) {
      if (!group) return
      const active    = this.activeLayerCode
      const layerCode = group.userData?.layerCode
      // Con resaltado activo, TODOS los carriles van a gris (el color de estado
      // se anula; manda el overlay de resaltado). Sin resaltado, solo se
      // agrisan los de las capas no-activas.
      const dim       = this.highlightMode
        ? true
        : !!(active && layerCode && layerCode !== active)
      const mats      = group.userData?.materials || []
      for (const m of mats) {
        if (!m) continue
        if (dim) {
          m.color.setHex(DIM_ADVANCE_GRAY_HEX)
          m.opacity = DIM_ADVANCE_OPACITY
        } else {
          const base = m.userData && m.userData.baseColorHex
          if (base != null) m.color.setHex(base)
          m.opacity = 0.9
        }
      }
    },

    /**
     * Reconstruye `filterLayers` (lo que muestra el panel de Capas) según la vista activa.
     * Solo incluye layers que pertenecen a `displayView` (o son layers de texto, siempre visibles).
     * Se invoca al abrir el panel y cuando cambia displayView estando el panel abierto.
     */
    _RefreshFilterLayers() {
      if (!this.$refs.viewer) return

      // En progress mode (cliente), las capas trackeables del .epcb tienen
      // como `name` el code numerado (electrical_n / trench_n). Para mostrar
      // un nombre humano en el panel, resolvemos `pictogram_layer.name`
      // por code. Internamente se mantiene `name = code` para que las
      // llamadas a viewer.ShowLayer(name) sigan funcionando.
      const nameByCode = Object.create(null)
      if (this.isProgressMode) {
        for (const pl of (this.pictogram?.layers || [])) {
          if (pl?.code && pl?.name) nameByCode[pl.code] = pl.name
        }
      }

      this.filterLayers = this.$refs.viewer.GetLayers(true)
        .filter(l => this._LayerBelongsToView(l.name, this.displayView))
        .map(l => ({
          name:        l.name,
          displayName: nameByCode[l.name] || l.displayName,
          color:       l.color,
          isVisible:   true,
        }))
    },

    /**
     * Cambia la vista activa (Vistas panel).
     * Aplica la visibilidad correspondiente al DxfViewer iterando todas sus layers.
     */
    _OnViewSelected(code) {
      this.displayView = code
      this._ApplyDisplayView(code)
    },

    /**
     * Itera todas las layers del DxfViewer y muestra/oculta según pertenezcan o no a la vista.
     */
    _ApplyDisplayView(code) {
      if (!this.$refs.viewer) return
      const all = this.$refs.viewer.GetLayers()
      for (const lyr of all) {
        this.$refs.viewer.ShowLayer(lyr.name, this._LayerBelongsToView(lyr.name, code))
      }
    },

    /**
     * Decide si una layer DXF pertenece al filtro de la vista seleccionada.
     * El worker prefija las layers procesadas con {TYPE}_EPC_ (PILINGS_EPC_, MODULES_EPC_, etc.).
     * Las layers ORIGINALES del DXF (sin _EPC_) corresponden a la vista "structures".
     *
     * Las layers que contienen TEXT/MTEXT son SIEMPRE visibles para mantener las etiquetas
     * (nombres de estructura) sobre el dibujo al cambiar de vista, sin duplicarse por
     * subdivisión: como los textos viven sobre la estructura padre, se muestran una sola vez.
     */
    _LayerBelongsToView(layerName, viewCode) {
      return layerBelongsToView(layerName, viewCode, this.textLayers)
    },

    _OnClickZoom(zoomIn) {
      if (!this.$refs.viewer) return
      if( zoomIn )
        this.$refs.viewer.ZoomIn()
      else
        this.$refs.viewer.ZoomOut()
    },

    _OnClickFullScreen() {
      const element = this.$refs.viewer_page
      if (isFullScreenActive()) exitFullScreen()
      else                      requestFullScreen(element)
    },

    _OnFitContent() {
      if (this.$refs.viewer) this.$refs.viewer.FitToScreen()
    },

    /**
     * Abre el modal de configuración de subdivisiones pasándole el cadJson completo.
     * El modal se encarga de extraer las capas y calcular el total de estructuras.
     */
    _HandleConfigureSubdivisions() {
      if (!this.cadJson) return
      this.$refs.refConfigureSubdivisionsModal.show(this.cadJson)
    },

    /**
     * Pregunta al usuario antes de resetear el visor.
     * Si confirma, dispara _DoReset() que limpia todo el estado del archivo cargado.
     */
    _HandleResetViewer() {
      this.$buefy.dialog.confirm({
        message:       this.$t('confirm_reset_viewer'),
        cancelText:    this.$t('cancel'),
        confirmText:   this.$t('accept'),
        type:          'is-warning',
        hasIcon:       true,
        icon:          'exclamation-triangle',
        iconPack:      'fas',
        onConfirm:     () => this._DoResetViewer(),
      })
    },
    /**
     * Resetea el estado del LayoutBuilder al inicial (sin archivo cargado).
     *   - uploadedFile y cadJson → null
     *   - hasFile (computed) pasa a false
     *   - El Toolbar se oculta automáticamente (v-if="hasFile")
     *   - El dropzone vuelve a aparecer (v-if="!hasFile")
     *   - El watcher del Viewer detecta el cambio a null y llama dxfViewer.Clear()
     *   - Reset del input invisible para permitir re-seleccionar el mismo archivo
     */
    _DoResetViewer() {
      this.uploadedFile = null
      this.cadJson      = null
      this.parseError   = null

      this.processedCadJson    = null
      this.totalEntitiesDesign = {}
      this.cadLayeredDesign    = null
      this.createElements      = { pilings: false, modules: false }

      this.showLayerPanel = false
      this.filterLayers   = []

      this.showPanelViews = false
      this.displayView    = DEFAULT_VIEW

      // Selección de capas (no-mechanical) — la próxima carga reconstruirá
      // la lista desde el nuevo DXF.
      this.dxfLayersInfo      = []
      this.selectedLayerCodes = []
      this.customLayerNames   = {}

      // Permite re-seleccionar el mismo archivo después
      if (this.$refs.fileInputHidden) {
        this.$refs.fileInputHidden.value = ''
      }
    },

    async _HandleClickSave() {
      // En mechanical_installation el procesamiento de subdivisiones (pilings,
      // modules, panels) es obligatorio antes de guardar — el pictograma no
      // tiene sentido sin esas entidades. Para el resto de tipos (electrical,
      // trenches, evacuation) el procesamiento no aplica y se puede guardar
      // directamente con el cadJson del parser.
      if (!this.hasProcessed && this.typePictogram === PICTOGRAM_TYPES.MECHANICAL) {
        this.$toast.open({
          message:  this.$t('process_first'),
          type:     'is-warning',
          position: 'is-top-right',
        })
        return
      }

      // En modo 'upload-new-version' no abrimos el modal de crear pictograma —
      // el pictograma ya existe. Disparamos el flujo de subir nueva versión.
      if (this.mode === 'upload-new-version') {
        await this._HandleClickContinueAsNewVersion()
        return
      }

      this.$refs.refSavePictogramModal.show()
    },

    /**
     * Stub temporal. En sub-paso C.4 implementamos:
     *   - Upload del DXF como nueva DocumentVersion
     *   - Upload del JSON parseado a temp S3
     *   - Redirect a la misma ruta con ?temp_key=...&doc_version_id=...
     *   - ProcessPictogramPage renderiza UpdatePictogramFlow al detectar los query params
     */
    async _HandleClickContinueAsNewVersion() {
      if (!this.pictogram?.id || !this.pictogram?.document_id) {
        this.$toast.open({
          message:  'Falta información del pictograma (id o document_id).',
          type:     'is-danger',
          position: 'is-top-right',
        })
        return
      }

      try {
        this.ShowLoader({ loading: true, text: this.$t('uploading_cad_file') })

        const { documentVersionId, sceneKey } = await uploadNewPictogramVersion({
          dxfFile:     this.uploadedFile,
          cadJson:     this.processedCadJson,
          documentId:  this.pictogram.document_id,
          pictogramId: this.pictogram.id,
          onProgress:  (key) => this.ShowLoader({ loading: true, text: this.$t(key) }),
        })

        // Redirect a la misma URL con query params — ProcessPictogramPage va a
        // detectar temp_key y renderizar el modo 'review' en sub-paso C.5.
        this.$router.replace({
          name: 'project.admin.pictogram.process-update',
          params: {
            project_id: this.entityId,
            id:         this.pictogram.id,
          },
          query: {
            temp_key:       sceneKey,
            doc_version_id: documentVersionId,
          },
        })
      } catch (err) {
        logger.error('upload new version error:', err)

        // Backend rechazó porque ya hay una versión pendiente para este pictograma.
        // Ofrecer al usuario ir al PictogramConfig donde puede continuar el review
        // o descartar la pendiente antes de reintentar.
        if (err.name === 'PendingVersionExists') {
          this.$buefy.dialog.confirm({
            title:        this.$t('pending_version_exists_title') || 'Versión pendiente',
            message:      err.message,
            confirmText:  this.$t('go_to_pictogram') || 'Ir al pictograma',
            cancelText:   this.$t('cancel') || 'Cancelar',
            type:         'is-warning',
            hasIcon:      true,
            onConfirm: () => {
              this.$router.replace({
                name: 'project.admin.pictogram.config',
                params: {
                  project_id: this.entityId,
                  id:         this.pictogram.id,
                },
              })
            },
          })
        } else {
          this.$toast.open({
            message:  this.$t(err.message) || err.message || this.$t('save_error'),
            type:     'is-danger',
            position: 'is-top-right',
          })
        }
      } finally {
        this.ShowLoader({ loading: false, text: null })
      }
    },

    _HandleOpenPanel(namePanel) {
      if (namePanel === 'panel-views')  this.showPanelViews = !this.showPanelViews
      if (namePanel === 'panel-layers') this.showLayerPanel = !this.showLayerPanel
    },

    /**
     * Navegación de "volver atrás" del subheader.
     * Si el pictograma trae info de actividad/sub-actividad, redirige a esa
     * vista (mismo patrón que viewer-pixi/ViewerPageKonva). Si no, usa el
     * historial del navegador como fallback.
     */
    _HandleReturnClick() {
      const activities = this.pictogram && this.pictogram.activities
      if (activities && activities.length > 0) {
        const first = activities[0]
        if (first.parent_id !== null) {
          this.$router.push({
            name: 'subactivity.home',
            params: { subactivity_id: first.id, activity_id: first.parent_id }
          })
        } else {
          this.$router.push({
            name: 'activity.home',
            params: { activity_id: first.id }
          })
        }
        return
      }
      // Fallback: volver a la página anterior del historial.
      this.$router.back()
    },

    // ───────────────── Handlers del SaveSuccessModal ─────────────────

    /** Cerrar el modal y quedar en la vista actual con el pictograma cargado. */
    _OnSaveSuccessClose() {
      // no-op: el usuario sigue trabajando con el pictograma actual
    },

    /** Limpiar el visor y volver al dropzone para cargar un nuevo DXF. */
    _OnSaveSuccessNew() {
      this._DoResetViewer()
    },

    /** Volver al listado de pictogramas (misma navegación que el botón "Atrás"). */
    _OnSaveSuccessBack() {
      this._HandleReturnClick()
    },

    _AllowedAction(action) {
      if (Object.keys(this.canActionsPictogram).length === 0)
        return false;

      return this.canActionsPictogram[action] === 'undefined' ? false : this.canActionsPictogram[action]
    },



    onSceneLoaded(e) {
      logger.info('scene loaded')
      // Aplicamos la vista activa inmediatamente al cargar la escena (antes del primer paint),
      // para evitar el flash en el que el viewer renderiza con TODAS las layers visibles y
      // medio segundo después se ocultan las que no pertenecen a la vista.
      // Solo aplica cuando ya hay processedCadJson (tras configurar subdivisiones).
      if (this.hasProcessed && this.$refs.viewer) {
        this._ApplyDisplayView(this.displayView)
      }
      // Flag para que el overlay interactivo se monte solo después de que la
      // escena Three.js esté lista (sino `$refs.viewer.GetDxfViewer()` devuelve
      // null). El template pasa `$refs.viewer` directo al overlay — NO se
      // guarda en data() porque Vue observaría el árbol completo del Viewer
      // (renderer, materials, geometries) y eso cuelga el thread.
      this.viewerReady = true

      // Avance lineal: solo en no-mechanical Y viewerMode === 'progress'.
      // En 'design' (admin) nunca se inicializa el overlay / hover tooltip —
      // el admin carga y guarda, el avance se registra desde el viewer cliente.
      if (this.typePictogram !== PICTOGRAM_TYPES.MECHANICAL && this.isProgressMode) {
        this.$nextTick(() => {
          this._RehydrateAdvance()
          this._BindHoverTooltip()
          // Si ya había activeLayerCode (por workLayer en la URL), recién
          // ahora podemos aplicar el dim sobre las capas ya cargadas.
          this._ApplyLayerDim()
        })
      }
    },

    /**
     * Construye la lista de capas que alimenta el SelectLayersModal combinando:
     *   - GetLayers(true) del DxfViewer → name/displayName/color por capa visible
     *   - computeLayerMetrics(cadJson)   → length y count por capa
     * Excluye las capas que sólo contienen TEXT/MTEXT (vienen de textLayers,
     * ya computado en otro computed por el panel de filtro).
     * Por defecto todas las capas arrancan seleccionadas en la primera apertura;
     * si el usuario vuelve a abrir el modal sin cambiar de DXF se preserva la
     * selección previa.
     */
    _BuildLayersList() {
      if (!this.$refs.viewer) {
        this.dxfLayersInfo      = []
        this.selectedLayerCodes = []
        return
      }
      const viewerLayers = this.$refs.viewer.GetLayers(true) || []
      const metrics      = computeLayerMetrics(this.cadJson)
      const textSet      = this.textLayers || new Set()

      this.dxfLayersInfo = viewerLayers
        .filter(l => !textSet.has(l.name))
        .map(l => {
          const m = metrics[l.name] || { length: 0, count: 0 }
          return {
            name:         l.name,
            displayName:  l.displayName,
            color:        l.color,
            entityCount:  m.count,
            lengthMeters: m.length,
          }
        })
      this.selectedLayerCodes = this.dxfLayersInfo.map(l => l.name)
    },

    onClick(e) {
      logger.info('click on scene:', e.position)
    },

    // ─── Avance lineal sobre polilíneas con ancho (solo no-mechanical) ───────
    //
    // Flow:
    //   1. DxfInteractiveOverlay emite epc-entity-click con {handle, entity}
    //   2. Validamos que sea polilínea con ancho > 0 y abrimos el modal
    //   3. Usuario elige dirección + metros, aplicamos → pintamos quads
    //      translúcidos encima del ribbon (THREE.Group por handle).
    //
    // Sin persistencia: al recargar/cambiar archivo se descartan los meshes.

    /**
     * Filtro del overlay — CRÍTICO para memoria.
     *
     * El overlay crea un hitbox (PlaneGeometry + Material) por cada segmento
     * de cada entity que pase este filtro. Un DXF puede traer decenas de
     * miles de polilíneas wireframe (símbolos en bloques, decoración) y
     * generar hitboxes para todas agota memoria del navegador.
     *
     * Como esta feature solo opera sobre polilíneas con ancho (las que el
     * render dibuja como ribbon), filtramos por `width > 0`. Eso descarta
     * el 99% de las polilíneas wireframe.
     */
    _AdvanceOverlayFilter(entity) {
      if (!entity) return false
      if (entity.type !== 'POLYLINE' && entity.type !== 'LWPOLYLINE') return false
      return this._GetEntityMaxWidth(entity) > 0
    },

    /** Máximo ancho declarado en la polilínea (constante o por vértice). */
    _GetEntityMaxWidth(entity) {
      let w = entity?.width || 0
      const verts = entity?.vertices || []
      for (const v of verts) {
        if (v.startWidth) w = Math.max(w, v.startWidth)
        if (v.endWidth)   w = Math.max(w, v.endWidth)
      }
      return w
    },

    /** Suma de longitudes de los segmentos de la polilínea. */
    _GetEntityTotalLength(entity) {
      const verts = entity?.vertices || []
      let total = 0
      for (let i = 0; i < verts.length - 1; i++) {
        const dx = verts[i + 1].x - verts[i].x
        const dy = verts[i + 1].y - verts[i].y
        total += Math.sqrt(dx * dx + dy * dy)
      }
      return total
    },

    /**
     * UN CLICK sobre una polilínea trackeable → popover de INFO (campos extra +
     * certificación por metros + progreso). El doble click abre el formulario
     * de avance (_OnEntityClickForAdvance). Mismos filtros de entidad/capa.
     */
    _OnEntityClickForInfo({ handle, entity, clientX, clientY }) {
      if (!entity || this.typePictogram === PICTOGRAM_TYPES.MECHANICAL) return
      if (this._GetEntityMaxWidth(entity) <= 0) return

      const pictogramLayer = (this.pictogram?.layers || []).find(l => l.code === entity.layer)
      if (!pictogramLayer) return
      if (this.activeLayerCode && entity.layer !== this.activeLayerCode) return

      // Ocultar el tooltip de hover para que no se pise con el popover.
      this.advanceTooltip.visible = false

      // Abrir el popover en la posición del click, en estado "cargando".
      this.infoPopover.position = { x: clientX || 0, y: clientY || 0 }
      this.infoPopover.data     = null
      this.infoPopover.loading  = true
      this.infoPopover.visible  = true

      // Contorno del segmento (line punteado + resaltado) siguiendo la cámara.
      this._TrackInfoSegment(entity)

      getElectricalComponentInfo(this.pictogram.id, String(handle), pictogramLayer.code)
        .then(({ data }) => {
          if (!this.infoPopover.visible) return
          this.infoPopover.data = (data && data.success) ? data.component : null
        })
        .catch((err) => {
          logger.error('getElectricalComponentInfo failed:', err)
          this.infoPopover.data = null
        })
        .finally(() => {
          this.infoPopover.loading = false
        })
    },

    /**
     * Proyecta el contorno del segmento a pantalla y lo mantiene sincronizado
     * (rAF) mientras el popover está abierto — así el resaltado + el leader line
     * siguen al segmento aunque el usuario haga pan/zoom.
     */
    _TrackInfoSegment(entity) {
      cancelAnimationFrame(this._infoRaf)
      const step = () => {
        if (this._isBeingDestroyed || this._isDestroyed || !this.infoPopover.visible) return
        const pts = this.$refs.refDxfOverlay?.ProjectEntityToScreen?.(entity) || []
        this.infoPopover.segmentPoints = pts
        if (pts.length) {
          let sx = 0, sy = 0
          for (const p of pts) { sx += p.x; sy += p.y }
          this.infoPopover.anchor = { x: sx / pts.length, y: sy / pts.length }
        } else {
          this.infoPopover.anchor = null
        }
        this._infoRaf = requestAnimationFrame(step)
      }
      this._infoRaf = requestAnimationFrame(step)
    },

    /** Cierra el popover de info y corta el loop de seguimiento del segmento. */
    _CloseInfoPopover() {
      this.infoPopover.visible = false
      cancelAnimationFrame(this._infoRaf)
      this.infoPopover.segmentPoints = []
      this.infoPopover.anchor = null
    },

    _OnEntityClickForAdvance({ handle, entity }) {
      if (!entity || this.typePictogram === PICTOGRAM_TYPES.MECHANICAL) return
      // Filtramos por ancho: solo polilíneas que el render dibuja como ribbon.
      const width = this._GetEntityMaxWidth(entity)
      if (width <= 0) return

      const geomLength = this._GetEntityTotalLength(entity)
      if (geomLength <= 0) return

      // Lookup del pictogram_layer al que pertenece esta polilínea.
      // `entity.layer` es el code que el encoder puso en el .epcb:
      //   - Capas trackeables (seleccionadas en admin): coincide con
      //     pictogram_layer.code (electrical_n / trench_n).
      //   - Capas de contexto visual: es el nombre DXF original, no
      //     matchea ningún pictogram_layer → no se permite registrar avance.
      //
      // Si el pictograma fue guardado antes de este cambio, sus polilíneas
      // tienen el nombre DXF como code y no van a matchear → re-guardar
      // desde admin para regenerar el .epcb con los codes correctos.
      const pictogramLayer = (this.pictogram?.layers || [])
        .find(l => l.code === entity.layer)
      if (!pictogramLayer) return

      // Filtro adicional: si el usuario activó una capa específica en el
      // LayersPanel, solo respondemos a clicks sobre esa capa. Esto evita
      // registrar avance por error sobre polilíneas de otras capas que
      // estén cerca o superpuestas.
      if (this.activeLayerCode && entity.layer !== this.activeLayerCode) return

      // Si ya hay avance guardado para ese handle precargamos los valores —
      // el modal funciona también como edición, no solo como alta.
      // Backward-compat: si el state viejo (sin segments) existe, lo
      // promovemos a un único segment para no perder data.
      const existing = this._UpgradeAdvanceState(this._advanceState[String(handle)])
      if (existing) this._advanceState[String(handle)] = existing

      // totalLength a 2 decimales (misma precisión que los metros que se
      // registran). CRÍTICO para la elegibilidad de estados: si usáramos la
      // longitud cruda de la geometría (ej. 27.8034) los metros (2 dec., máx
      // 27.80) nunca la alcanzarían y `cap` nunca llegaría a 0 → el estado
      // completo no se bloquearía ni habilitaría el siguiente. Al reabrir un
      // segmento con avance usamos su total_length guardado (ya redondeado).
      const totalLength = (existing && existing.totalLength != null)
        ? Number(existing.totalLength)
        : Math.round(geomLength * 100) / 100

      // Resolver los statuses que verá el usuario en el select:
      //   - Si pictogram_layer tiene statuses configurados → usarlos
      //     (cada uno con su id, name y color reales de la BD).
      //   - Si no → fallback al array hardcoded ADVANCE_STATUSES.
      const statuses = this._GetStatusesForLayer(pictogramLayer)

      // entity/handle/totalLength/pictogramLayer/statuses en propiedad de
      // instancia (no reactiva): el entity tiene arrays de vertices que si
      // Vue observara recursivamente colgaría el thread. Los statuses los
      // guardamos para resolver color al pintar y persistir avance.
      this._advanceCurrent = { handle, entity, totalLength, pictogramLayer, statuses }

      this.$refs.refRegisterAdvanceModal.show({
        totalLength,
        vertices:  entity.vertices,
        direction: existing?.direction || 'l2r',
        directionLocked: !!existing,
        // Modelo de segmentos: cada apply agrega un tramo nuevo, no
        // modifica los existentes. El modal los renderiza como barra
        // multi-color + lista por status.
        previousSegments: existing?.segments || [],
        statuses,
        // Modo de orden de estados (free | strict | capped) resuelto con
        // herencia capa → pictograma → default. El modal usa esto para
        // deshabilitar estados no elegibles y capar el máximo del input.
        orderMode: this._ResolveStatusOrderMode(pictogramLayer),
      })
    },

    /**
     * Resuelve el modo de orden de estados para una capa, con herencia:
     * capa (metadata.status_order_mode) → pictograma
     * (metadata.progress_viewer.status_order_mode) → default 'strict'.
     */
    _ResolveStatusOrderMode(pictogramLayer) {
      const MODES = ['free', 'strict', 'capped']
      const layerMode = pictogramLayer && pictogramLayer.metadata && pictogramLayer.metadata.status_order_mode
      if (MODES.includes(layerMode)) return layerMode
      const picMode = this.pictogram && this.pictogram.metadata
        && this.pictogram.metadata.progress_viewer
        && this.pictogram.metadata.progress_viewer.status_order_mode
      if (MODES.includes(picMode)) return picMode
      return 'strict'
    },

    /**
     * Backward-compat: convierte un state viejo (sin `segments`, con
     * `meters/status` planos) al formato nuevo con array de tramos.
     * Para state ya nuevo, no hace nada. Para null/undefined, retorna null.
     */
    _UpgradeAdvanceState(state) {
      if (!state) return null
      if (Array.isArray(state.segments)) return state
      // Promoción: el state viejo tenía un único registro plano. Lo
      // representamos como un único segment.
      if (state.meters > 0) {
        return {
          direction:   state.direction || 'l2r',
          totalLength: state.totalLength || 0,
          segments: [{
            meters:      state.meters,
            status:      state.status,
            statusLabel: state.statusLabel || '',
            statusColor: state.statusColor || null,
            createdAt:   Date.now(),
          }],
        }
      }
      return null
    },

    /**
     * Devuelve los statuses disponibles para registrar avance sobre una capa.
     * Formato uniforme: `[{ value, label, color }]`.
     *
     *   - Si la capa tiene `statuses` configurados desde BD (vienen filtrados
     *     por type='status' gracias al modelo PictogramLayer): los mapeamos
     *     usando `id` como value, `name` como label y `color` como color real.
     *   - Si no: usamos el array hardcoded ADVANCE_STATUSES con labels i18n.
     */
    _GetStatusesForLayer(pictogramLayer) {
      const raw = pictogramLayer?.statuses
      if (Array.isArray(raw) && raw.length > 0) {
        return raw.map(s => ({
          value: s.id,
          label: s.name,
          color: s.color || '#9ca3af',
          // `weight` define el signo del estado: < 0 = rechazo (rama negativa),
          // >= 0 o null = paso positivo. Lo usa RegisterAdvanceModal.statusEligibility
          // para resolver el predecesor real (el positivo anterior, saltando rechazos).
          weight: s.weight != null ? Number(s.weight) : null,
        }))
      }
      // Fallback hardcoded — convertimos el color int a CSS hex.
      return ADVANCE_STATUSES.map(s => ({
        value: s.value,
        label: this.$t('advance_status_' + s.value),
        color: '#' + s.color.toString(16).padStart(6, '0'),
      }))
    },

    /**
     * Handler del evento epc-apply de RegisterAdvanceModal.
     * El modelo es acumulativo por segmentos: cada Apply agrega un nuevo
     * tramo (no modifica los existentes).
     *
     * @param {Object} payload
     * @param {String} payload.direction   — 'l2r' | 'r2l' (solo se setea la
     *                                       primera vez; las siguientes ignoradas)
     * @param {Number} payload.meters      — DELTA: metros nuevos en este registro
     * @param {*}      payload.status      — value del status (id numérico o string)
     * @param {String} payload.statusLabel — label legible para tooltip/breakdown
     * @param {String} payload.statusColor — color hex para pintar el tramo
     */
    async _OnAdvanceApply({ direction, meters, status, statusLabel, statusColor, startDate }) {
      const current = this._advanceCurrent
      if (!current) return
      const { totalLength, pictogramLayer } = current
      const delta = Math.max(0, Number(meters) || 0)
      if (delta <= 0) return
      const key = String(current.handle)

      // Obtener (o inicializar) el state acumulativo del handle.
      let state = this._UpgradeAdvanceState(this._advanceState[key])
      if (!state) {
        state = {
          direction,
          totalLength,
          segments: [],
        }
      }

      // Clampear el delta (red de seguridad; el modal ya limita al mismo tope).
      // En capas CON rechazos: MODELO DE BOLSAS (flujo parcial con retorno). El tope
      // de cada acción sale de la bolsa que consume: Ejecutar = por-iniciar+rechazado;
      // Aceptar/Rechazar del nivel k = bolsa del nivel anterior (bags[k-1]). Ver
      // _SimulatePools. En capas sin rechazos (clásico), cada estado es un proceso
      // paralelo: no exceder totalLength menos lo acumulado.
      const hasRejects = (current.statuses || []).some(s => Number(s.weight) < 0)
      const isRejectStatus = (v) => {
        const s = (current.statuses || []).find(x => x.value === v)
        return s && Number(s.weight) < 0
      }
      let clamped
      if (hasRejects) {
        const pools = this._SimulatePools(state.segments, current.statuses, totalLength)
        const idx = (current.statuses || []).findIndex(s => s.value === status)
        let cap = 0
        if (idx >= 0) {
          if (isRejectStatus(status)) {
            // Rechazo: consume de la bolsa del nivel anterior a su accept emparejado.
            const acc = pools.prevIdx[idx]
            const ka = (acc >= 0 && pools.posLevelOfIdx[acc] !== undefined) ? pools.posLevelOfIdx[acc] : -1
            cap = (ka >= 1) ? (pools.bags[ka - 1] || 0) : 0
          } else {
            const pk = pools.posLevelOfIdx[idx]
            cap = (pk === 0) ? ((pools.unstarted || 0) + (pools.rejected || 0)) : (pools.bags[pk - 1] || 0)
          }
        }
        clamped = Math.min(delta, Math.max(0, cap))
      } else {
        const usedForStatus = state.segments
          .filter(s => s.status === status)
          .reduce((acc, s) => acc + (s.meters || 0), 0)
        clamped = Math.min(delta, Math.max(0, totalLength - usedForStatus))
      }
      if (clamped <= 0) return

      // Resolver status_id real (solo si es numérico — viene de
      // pictogram_layer_statuses). Cuando es string ('in_progress', etc.)
      // estamos en fallback hardcoded → mandamos null al backend y el
      // label/color caché lo permite mostrar igualmente.
      const statusId = typeof status === 'number' ? status : null

      // 1. POST al backend PRIMERO. Solo si éxito persistimos local y pintamos.
      const modalRef = this.$refs.refRegisterAdvanceModal
      if (this.pictogram?.id && pictogramLayer?.id) {
        try {
          await registerLayerProgress(this.pictogram.id, pictogramLayer.id, {
            dxf_handle:        key,
            total_length:      totalLength,
            direction:         state.direction,
            executed_quantity: clamped,
            status_id:         statusId,
            start_date:        startDate,
          })
        } catch (err) {
          logger.error('registerLayerProgress failed:', err)
          // Si el backend rechazó por la regla de orden de estados (422 de
          // ValidationException), mostramos su mensaje específico; sino, genérico.
          const data = err && err.response && err.response.data
          const backendMsg = (data && data.errors && data.errors.status_id && data.errors.status_id[0])
            || (data && data.message)
          modalRef?.setError?.(backendMsg || this.$t('error_saving_progress'))
          return  // Sin éxito → no pintamos ni cacheamos.
        }
      }

      // 2. Persistencia local (cache transitoria de la sesión) + paint.
      state.segments.push({
        meters:      clamped,
        status,
        statusLabel,
        statusColor,
        createdAt:   Date.now(),
      })
      this._advanceState[key] = state
      this._PersistAdvanceState()

      // Fuente de entity: el del cadJson por handle (misma que usa el
      // rehidratado). `current.entity` (raycast del overlay) queda de fallback.
      const paintEntity = this._GetEntityByHandle(current.handle) || current.entity

      this._PaintAdvanceForEntity(current.handle, paintEntity, state.direction, state.segments)
      // Si hay un resaltado activo, este handle pudo cambiar de grupo
      // (pasó de "sin avance" a "con avance") — recalculamos el resaltado.
      if (this.highlightMode) this._ApplyHighlight()
      // 3. Cerrar el modal solo después del paint exitoso.
      modalRef?.hide?.()

      // 4. Re-hidratar desde el backend — MISMO camino que el refresh (que SÍ se
      // visualiza). registerLayerProgress persistió el nuevo estado; volvemos a
      // consultar getPictogramComponents (estado canónico de cada segmento) y
      // re-pintamos todo. Ventaja extra: al ser async, el re-pintado ocurre tras
      // el fetch, con el modal ya cerrado y el layout asentado (el "buen momento"
      // de render que en el reload ocurre solo).
      this._RehydrateAdvance()
    },

    /**
     * Estado ACTUAL a pintar de una polilínea con flujo de rechazos: el ÚLTIMO
     * estado registrado cronológicamente, pintado sobre sus METROS NETOS reales
     * (el avance es parcial y acumulativo: 10 + 20 = 30). Un RECHAZO rechaza TODO
     * → resetea el neto a 0; pero SÍ se pinta con el COLOR DEL RECHAZO sobre los
     * metros que fueron rechazados (el neto que había justo antes del rechazo; al
     * 100% = todo el tramo). Al registrar un nuevo avance después, el neto arranca
     * de 0 y el color positivo nuevo reemplaza al del rechazo. Si aún no hay avance
     * neto → []. Nota: el reset del neto para el TOOLTIP (_OnAdvanceHover) y la
     * ELEGIBILIDAD (statusEligibility) sigue igual — esta función solo pinta.
     */
    /**
     * Simula el historial de un segmento (capas con rechazos) como transferencias
     * entre BOLSAS y devuelve el estado vigente. MISMA lógica que
     * RegisterAdvanceModal._SimulatePools (mantener en sync):
     *   - Ejecutar (nivel 0): de (rechazado→por-iniciar) a bag[0].
     *   - Aceptar nivel k≥1: de bag[k-1] a bag[k].
     *   - Rechazar (emparejado con accept de nivel k): de bag[k-1] a `rejected`
     *     (re-ejecutable; NO resetea). Suma total = totalLength.
     * @returns {{L,positives,posLevelOfIdx,prevIdx,M,bags,unstarted,rejected}}
     */
    _SimulatePools(segments, statuses, totalLength) {
      const L = totalLength || 0
      const sts = statuses || []
      const isReject = (i) => Number(sts[i].weight) < 0
      const positives = []
      const posLevelOfIdx = {}
      const prevIdx = []
      let lastPos = -1
      for (let i = 0; i < sts.length; i++) {
        prevIdx[i] = lastPos
        if (!isReject(i)) { posLevelOfIdx[i] = positives.length; positives.push(i); lastPos = i }
      }
      const M = positives.length
      const bags = new Array(M).fill(0)
      let unstarted = L
      let rejected = 0
      // Rechazados POR ESTADO (cada estado de rechazo conserva su color/label); orden
      // de inserción = FIFO al re-ejecutar. `rejected` = total (= suma del map).
      const rejectedByStatus = new Map()
      const consumeRejected = (amount) => {
        let toReduce = amount
        for (const [sv, val] of rejectedByStatus) {
          if (toReduce <= 1e-9) break
          const r = Math.min(val, toReduce)
          rejectedByStatus.set(sv, val - r)
          toReduce -= r
        }
      }
      const valueToIdx = new Map(sts.map((s, i) => [s.value, i]))
      const segs = [...(segments || [])].sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0))
      for (const seg of segs) {
        const i = valueToIdx.get(seg.status)
        if (i === undefined) continue
        let m = Math.max(0, Number(seg.meters) || 0)
        if (m <= 0) continue
        if (!isReject(i)) {
          const k = posLevelOfIdx[i]
          if (k === 0) {
            const fromRej = Math.min(m, rejected); rejected -= fromRej; m -= fromRej
            if (fromRej > 0) consumeRejected(fromRej)
            const fromNew = Math.min(m, unstarted); unstarted -= fromNew; m -= fromNew
            bags[0] += (fromRej + fromNew)
          } else {
            const take = Math.min(m, bags[k - 1]); bags[k - 1] -= take; bags[k] += take
          }
        } else {
          const acceptIdx = prevIdx[i]
          const kAccept = (acceptIdx >= 0 && posLevelOfIdx[acceptIdx] !== undefined) ? posLevelOfIdx[acceptIdx] : 0
          const inputLevel = kAccept - 1
          if (inputLevel >= 0) {
            const take = Math.min(m, bags[inputLevel]); bags[inputLevel] -= take; rejected += take
            const sv = sts[i].value
            rejectedByStatus.set(sv, (rejectedByStatus.get(sv) || 0) + take)
          }
        }
      }
      return { L, positives, posLevelOfIdx, prevIdx, M, bags, unstarted, rejected, rejectedByStatus }
    },

    /**
     * Distribución VIGENTE de un segmento (capas con rechazos) para PINTAR, según el
     * modelo de bolsas (_SimulatePools). Devuelve la lista de tramos a pintar
     * CONCATENADOS a lo largo de la polilínea, en orden: etapas más avanzadas primero
     * (nivel alto → bajo) y al final los rechazados. Lo "por iniciar" NO se incluye
     * (queda sin pintar → se ve la capa base gris). Cada item: {status, statusLabel,
     * statusColor, meters}.
     */
    _CurrentStateSegments(segments, statuses, totalLength) {
      const pools = this._SimulatePools(segments, statuses, totalLength)
      const eps = 1e-6
      const out = []
      // Etapas positivas con metros vigentes, de la más avanzada a la menos.
      for (let k = pools.M - 1; k >= 0; k--) {
        if ((pools.bags[k] || 0) > eps) {
          const st = statuses[pools.positives[k]]
          out.push({ status: st.value, statusLabel: st.label, statusColor: st.color, meters: pools.bags[k] })
        }
      }
      // Rechazados (re-ejecutables): un tramo por CADA estado de rechazo, con su propio
      // color/label (no un color único). Orden de inserción = orden cronológico de rechazo.
      if (pools.rejectedByStatus) {
        for (const [sv, meters] of pools.rejectedByStatus) {
          if ((meters || 0) <= eps) continue
          const rej = (statuses || []).find(s => s.value === sv)
          out.push({
            status:      sv,
            statusLabel: rej ? rej.label : '',
            statusColor: (rej && rej.color) || '#e17055',
            meters,
          })
        }
      }
      return out
    },

    /**
     * Pinta un THREE.Group con quads translúcidos sobre los primeros `meters`
     * de la polilínea, partiendo del extremo elegido. Reemplaza el overlay
     * anterior si existía para el mismo handle.
     */
    /**
     * Pinta el avance acumulado sobre la polilínea como CARRILES PARALELOS,
     * uno por status. Cada status es un proceso independiente que arranca
     * desde el extremo de inicio (según direction):
     *
     *   "Cavar"  → 50m desde el extremo A (carril superior)
     *   "Tapar"  → 15m desde el extremo A (carril central)
     *   "Fibra"  → 10m desde el extremo A (carril inferior)
     *
     * El ancho del ribbon se divide proporcionalmente entre N status.
     * Si solo hay 1 status, ocupa todo el ancho.
     */
    _PaintAdvanceForEntity(handle, entity, direction, segments) {
      const dxf = this.$refs.viewer?.GetDxfViewer?.()
      if (!dxf?.HasRenderer?.()) return
      const scene  = dxf.GetScene?.()
      const origin = dxf.GetOrigin?.() || { x: 0, y: 0 }
      if (!scene) return

      this._DisposeAdvanceMesh(handle)

      // Capas CON rechazos (modelo de bolsas): pintamos la DISTRIBUCIÓN vigente del
      // segmento CONCATENADA a lo largo de la polilínea (franja a ancho completo):
      // etapas avanzadas primero, luego los rechazados; lo "por iniciar" queda SIN
      // pintar (se ve la capa base gris). Se actualiza en cada apply. Capas SIN
      // rechazos (zanjas: cavar/tapar/fibra) → carriles PARALELOS acumulativos.
      const _paintLayer = (this.pictogram?.layers || []).find(l => l.code === entity?.layer)
      const _paintStatuses = _paintLayer ? this._GetStatusesForLayer(_paintLayer) : []
      const isRejectLayer = _paintStatuses.some(s => Number(s.weight) < 0)

      let items
      if (isRejectLayer) {
        items = this._CurrentStateSegments(segments, _paintStatuses, this._GetEntityTotalLength(entity))
      } else {
        // Filtro de visualización de estados: por defecto solo el último
        // registrado; el usuario puede pedir "todos" o un estado puntual.
        items = this._FilterSegmentsForView(segments)
      }
      if (!Array.isArray(items) || items.length === 0) {
        dxf.Render?.()
        return
      }

      // r2l: invertimos para que los avances crezcan desde el extremo opuesto.
      const verts = (direction === 'r2l')
        ? entity.vertices.slice().reverse()
        : entity.vertices

      const polylineWidth = Math.max(this._GetEntityMaxWidth(entity), 0.1)

      const group = new THREE.Group()
      group.name = 'AdvanceOverlay_' + handle
      group.userData.handle = String(handle)
      // Capa (code) a la que pertenece este carril — la usa _DimAdvanceGroup
      // para atenuar los avances de las capas no-activas cuando hay una capa
      // activa en el LayersPanel (progress mode).
      group.userData.layerCode = entity?.layer || null
      group.userData.materials = []

      if (isRejectLayer) {
        // CONCATENADO a lo largo del tramo (ancho completo): cada bolsa ocupa su
        // longitud consecutiva desde el extremo de inicio (offset acumulado).
        let offsetMeters = 0
        for (let i = 0; i < items.length; i++) {
          const item = items[i]
          const colorInt = this._ResolveStatusColor(item)
          const material = this._MakeAdvanceMaterial(colorInt)
          group.userData.materials.push(material)
          const statusMeta = {
            status:      item.status,
            statusLabel: item.statusLabel,
            statusColor: item.statusColor,
            meters:      item.meters,
          }
          this._PaintAdvanceLane(group, verts, origin, item.meters, polylineWidth, 0, material, i, statusMeta, offsetMeters)
          offsetMeters += item.meters
        }
      } else {
        // Agrupar por status manteniendo el orden de PRIMERA APARICIÓN — eso
        // estabiliza los carriles entre re-pintados (el carril del primer
        // status no se mueve al agregar nuevos statuses).
        const byStatusOrdered = []
        const statusIndex = new Map()
        for (const seg of items) {
          const key = seg.status
          if (!statusIndex.has(key)) {
            statusIndex.set(key, byStatusOrdered.length)
            byStatusOrdered.push({
              status:      seg.status,
              statusLabel: seg.statusLabel || '',
              statusColor: seg.statusColor || null,
              meters:      0,
            })
          }
          byStatusOrdered[statusIndex.get(key)].meters += seg.meters || 0
        }

        const N = byStatusOrdered.length
        // Si hay N status, cada carril ocupa polylineWidth/N. Reservamos un
        // 10% de ese espacio como gap entre carriles para distinguirlos.
        const laneSlot  = polylineWidth / N
        const laneWidth = laneSlot * 0.9

        for (let i = 0; i < N; i++) {
          const item = byStatusOrdered[i]
          // Offset perpendicular al eje del segmento: distribuimos los N
          // carriles simétricamente alrededor del centro de la polilínea.
          const offsetY = (i - (N - 1) / 2) * laneSlot

          const colorInt = this._ResolveStatusColor(item)
          const material = this._MakeAdvanceMaterial(colorInt)
          group.userData.materials.push(material)

          // Datos del status que se guardan en cada mesh del carril para que
          // al hover el tooltip pueda mostrar info SÓLO de ese status.
          const statusMeta = {
            status:      item.status,
            statusLabel: item.statusLabel,
            statusColor: item.statusColor,
            meters:      item.meters,
          }

          this._PaintAdvanceLane(group, verts, origin, item.meters, laneWidth, offsetY, material, i, statusMeta)
        }
      }

      scene.add(group)
      this._advanceMeshes[handle] = group
      // Los materiales se crean siempre a opacidad plena (0.9); si hay una
      // capa activa distinta a la de este grupo, hay que atenuarlo ahora para
      // que un repintado (registro nuevo, rehidratado desde backend) no
      // "encienda" de nuevo los avances de las capas no-activas.
      this._DimAdvanceGroup(group)
      dxf.Render?.()
    },

    /**
     * Pinta un único carril a lo largo de los `verts` para cubrir `lengthToCover`
     * metros, con `laneWidth` de espesor y desplazado perpendicularmente al
     * eje del segmento por `offsetY`.
     *
     * Internamente itera los pares (vᵢ, vᵢ₊₁) consumiendo metros del total
     * y armando un quad por sub-tramo. El offset se aplica usando el vector
     * normal al segmento, así funciona correctamente en polilíneas con giros.
     */
    _PaintAdvanceLane(group, verts, origin, lengthToCover, laneWidth, offsetY, material, statusOrderIndex, statusMeta, startOffset = 0) {
      let skip = Math.max(0, startOffset)   // metros a saltar desde el inicio antes de pintar
      let remaining = lengthToCover
      for (let i = 0; i < verts.length - 1 && remaining > 1e-9; i++) {
        const a = verts[i], b = verts[i + 1]
        if (!Number.isFinite(a?.x) || !Number.isFinite(a?.y) ||
            !Number.isFinite(b?.x) || !Number.isFinite(b?.y)) continue

        const ax = a.x - origin.x, ay = a.y - origin.y
        const bx = b.x - origin.x, by = b.y - origin.y
        const dx = bx - ax, dy = by - ay
        const segLen = Math.sqrt(dx * dx + dy * dy)
        if (!Number.isFinite(segLen) || segLen < 1e-9) continue

        // Saltamos los primeros `skip` metros (para pintar concatenado desde un
        // offset a lo largo de la polilínea).
        let localStart = 0
        if (skip > 1e-9) {
          const s = Math.min(skip, segLen)
          skip -= s
          localStart = s
          if (segLen - localStart <= 1e-9) continue // este tramo se saltó entero
        }

        const avail = segLen - localStart
        const used  = Math.min(avail, remaining)
        if (used <= 1e-9) continue

        // Parámetros t inicial/final del sub-tramo pintado sobre este par de verts.
        const t0 = localStart / segLen
        const t1 = (localStart + used) / segLen
        // Centro del quad sobre el eje del segmento (punto medio de [t0, t1]).
        const tMid = (t0 + t1) * 0.5
        const cx_axis = ax + dx * tMid
        const cy_axis = ay + dy * tMid

        // Vector normal al segmento (perpendicular hacia "arriba" en
        // coordenadas locales del segmento).
        const nx = -dy / segLen
        const ny =  dx / segLen

        // Aplicamos offsetY a lo largo de la normal para colocar el carril.
        const cx = cx_axis + nx * offsetY
        const cy = cy_axis + ny * offsetY

        const geometry = new THREE.PlaneGeometry(used, laneWidth)
        const mesh = new THREE.Mesh(geometry, material)
        // Z escalonado por orden de carril para evitar z-fighting visual:
        // el carril 0 queda apenas más arriba que el 1, etc. La diferencia
        // (0.01) es invisible para el zoom típico pero estable para Three.js.
        mesh.position.set(cx, cy, 0.5 + statusOrderIndex * 0.01)
        mesh.rotation.z = Math.atan2(dy, dx)
        mesh.renderOrder = 600 + statusOrderIndex
        // Guardamos info del status en el mesh para que el hover/tooltip
        // pueda filtrar al status correspondiente sin necesidad de mapear.
        if (statusMeta) {
          mesh.userData.statusValue = statusMeta.status
          mesh.userData.statusLabel = statusMeta.statusLabel
          mesh.userData.statusColor = statusMeta.statusColor
          mesh.userData.statusMeters = statusMeta.meters
        }
        group.add(mesh)

        remaining -= used
      }
    },

    /**
     * Resuelve el color del status para un segment.
     *   1. Si hay statusColor (hex) en el segment, usar eso.
     *   2. Sino, fallback a ADVANCE_STATUSES hardcoded por value.
     */
    _ResolveStatusColor(segment) {
      if (segment?.statusColor) {
        return parseInt(String(segment.statusColor).replace('#', ''), 16) || 0x00b894
      }
      const def = ADVANCE_STATUSES.find(s => s.value === segment?.status) || ADVANCE_STATUSES[1]
      return def.color
    },

    /** Material reutilizable por color para los carriles de avance.
     *  Opacidad alta (0.9) para que el color del status domine sobre el
     *  ribbon gris del DxfViewer y no se vea "fusionado" con éste. */
    _MakeAdvanceMaterial(colorInt) {
      const mat = new THREE.MeshBasicMaterial({
        color:       colorInt,
        transparent: true,
        opacity:     0.9,
        depthTest:   false,
        depthWrite:  false,
        side:        THREE.DoubleSide,
      })
      // Guardamos el color de estado original para poder restaurarlo cuando el
      // carril vuelve a ser de la capa activa (ver _DimAdvanceGroup).
      mat.userData.baseColorHex = colorInt
      return mat
    },

    _DisposeAdvanceMesh(handle) {
      const group = this._advanceMeshes[handle]
      if (!group) return
      const dxf   = this.$refs.viewer?.GetDxfViewer?.()
      const scene = dxf?.GetScene?.()
      if (scene) scene.remove(group)
      // Disponer geometries de cada quad + uno o más materials (uno por
      // segment con color distinto, guardados en userData.materials).
      group.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose?.()
      })
      const mats = group.userData.materials || []
      for (const m of mats) m?.dispose?.()
      // Compat: si quedaba el campo legacy de un solo material, también.
      group.userData.sharedMaterial?.dispose?.()
      delete this._advanceMeshes[handle]
      dxf?.Render?.()
    },

    _DisposeAllAdvanceMeshes() {
      if (!this._advanceMeshes) return
      for (const h of Object.keys(this._advanceMeshes)) {
        this._DisposeAdvanceMesh(h)
      }
    },

    // ─── Filtro de visualización de estados (dropdown "Ver avance") ───────

    /**
     * Handler del dropdown "Ver avance". `option` es { value, ... }:
     *   'last' | 'all' | <statusValue>. Cambia el modo y repinta todo el
     *   avance con el filtro nuevo.
     */
    _OnStatusViewSelected(option) {
      const v = option && option.value
      if (v == null) return
      this.statusViewMode = v
      this._RepaintAllAdvance()
    },

    /**
     * Repinta todos los avances aplicando el filtro de estados actual.
     * _PaintAdvanceForEntity ya descarta el mesh previo de cada handle, así
     * que basta con recorrer el state y repintar.
     */
    _RepaintAllAdvance() {
      this._PaintAllAdvance()
    },

    /**
     * Filtra los segmentos a pintar según `statusViewMode`:
     *   - 'all'  → todos.
     *   - 'last' → solo los del ÚLTIMO estado registrado (el segmento con
     *     mayor createdAt define el estado; se pintan todos los de ese estado).
     *   - <value> → solo los de ese estado puntual.
     */
    _FilterSegmentsForView(segments) {
      if (!Array.isArray(segments) || segments.length === 0) return segments
      const mode = this.statusViewMode || 'last'
      if (mode === 'all') return segments
      if (mode === 'last') {
        let last = segments[0]
        for (const s of segments) {
          if ((s.createdAt || 0) >= (last.createdAt || 0)) last = s
        }
        return segments.filter(s => s.status === last.status)
      }
      // Estado puntual — comparamos como string para tolerar id numérico o
      // value string del fallback hardcoded.
      return segments.filter(s => String(s.status) === String(mode))
    },

    // ─── Resaltado del footer (dropdown "Resaltar") ───────────────────────
    //
    // Dos filtros visuales de un vistazo sobre las polilíneas trackeables
    // (width > 0 y capa que matchea un pictogram_layer):
    //   - 'without': color uniforme sobre las que NO tienen avance (pendientes)
    //   - 'with':    color uniforme sobre las que SÍ tienen avance
    // Es toggle: reelegir el modo activo lo apaga. Los meshes viven en
    // `_highlightMeshes` (aparte de `_advanceMeshes`) para no interferir con
    // los carriles de avance ni con el dim por capa.

    /**
     * Handler del dropdown "Resaltar". `option` es la opción emitida por
     * SplitActionDropdown ({ value, label, ... }). Toggle: si el modo elegido
     * ya está activo, se apaga; si no, se aplica el nuevo.
     */
    _OnHighlightSelected(option) {
      const mode = option && option.value
      if (mode !== 'without' && mode !== 'with') return
      if (this.highlightMode === mode) {
        this._ClearHighlight()
        return
      }
      this.highlightMode = mode
      this._ApplyHighlight()
    },

    /** Apaga el resaltado y descarta sus meshes; restaura el gris por capa. */
    _ClearHighlight() {
      this.highlightMode = null
      this._DisposeAllHighlightMeshes()
      // Vuelve al estado normal: la capa activa recupera color, las no-activas
      // quedan en gris (y ya no "todo gris").
      this._ApplyLayerDim()
    },

    /**
     * (Re)pinta el resaltado según `highlightMode`. Primero recalcula el gris
     * de toda la escena (con resaltado activo TODO va a gris vía _ApplyLayerDim)
     * y ENCIMA pinta un ribbon de color uniforme sobre las polilíneas que
     * cumplen el filtro (con/sin avance). Idempotente: descarta lo anterior.
     */
    _ApplyHighlight() {
      this._DisposeAllHighlightMeshes()
      // Poner todo en gris (o restaurar) según el modo actual, ANTES de pintar
      // el resaltado encima. Así el color de resaltado es lo único que resalta.
      this._ApplyLayerDim()
      const mode = this.highlightMode
      if (!mode) return
      const dxf = this.$refs.viewer?.GetDxfViewer?.()
      if (!dxf?.HasRenderer?.()) return

      const colorInt     = mode === 'with' ? HIGHLIGHT_COLORS.with : HIGHLIGHT_COLORS.without
      const wantProgress = mode === 'with'

      for (const entity of this._GetTrackableEntities()) {
        const handle = String(entity.handle)
        if (this._EntityHasProgress(handle) !== wantProgress) continue
        this._PaintHighlightForEntity(handle, entity, colorInt)
      }
      dxf.Render?.()
    },

    /**
     * Polilíneas trackeables del diseño actual: POLYLINE/LWPOLYLINE con
     * width > 0 cuya capa (code) matchea un pictogram_layer. Mismo criterio
     * que usa el overlay de clicks para permitir registrar avance.
     */
    _GetTrackableEntities() {
      const cadJson = this.processedCadJson || this.cadJson
      if (!cadJson) return []
      const raw  = cadJson.entities
      const list = !raw ? [] : (Array.isArray(raw) ? raw : Object.values(raw))
      const layerCodes = new Set((this.pictogram?.layers || []).map(l => l.code))
      const out = []
      for (const e of list) {
        if (!e || (e.type !== 'POLYLINE' && e.type !== 'LWPOLYLINE')) continue
        if (this._GetEntityMaxWidth(e) <= 0) continue
        if (!layerCodes.has(e.layer)) continue
        out.push(e)
      }
      return out
    },

    /**
     * Busca una entity del cadJson por su handle. Fuente única y fiable para
     * resolver la longitud total de una polilínea (independiente de qué mesh
     * haya golpeado el raycaster del hover).
     */
    _GetEntityByHandle(handle) {
      const cadJson = this.processedCadJson || this.cadJson
      if (!cadJson) return null
      const raw  = cadJson.entities
      const list = !raw ? [] : (Array.isArray(raw) ? raw : Object.values(raw))
      const h = String(handle)
      for (const e of list) {
        if (e && String(e.handle) === h) return e
      }
      return null
    },

    /** True si el handle tiene al menos un segmento de avance con metros > 0. */
    _EntityHasProgress(handle) {
      const state = this._advanceState?.[String(handle)]
      if (!state || !Array.isArray(state.segments)) return false
      return state.segments.some(s => (s.meters || 0) > 0)
    },

    /**
     * Pinta un ribbon de color uniforme cubriendo toda la polilínea. Reusa
     * _PaintAdvanceLane (un único carril a ancho completo, offset 0) y luego
     * eleva los quads por encima de los carriles de avance para que el color
     * uniforme domine visualmente.
     */
    _PaintHighlightForEntity(handle, entity, colorInt) {
      const dxf    = this.$refs.viewer?.GetDxfViewer?.()
      const scene  = dxf?.GetScene?.()
      const origin = dxf?.GetOrigin?.() || { x: 0, y: 0 }
      if (!scene) return

      const totalLength = this._GetEntityTotalLength(entity)
      if (totalLength <= 0) return
      // Resaltado un poco más ancho que la línea original (más visible).
      const width = Math.max(this._GetEntityMaxWidth(entity), 0.1) * HIGHLIGHT_WIDTH_FACTOR

      const group = new THREE.Group()
      group.name = 'HighlightOverlay_' + handle
      const fillMat = this._MakeHighlightMaterial(colorInt)
      const edgeMat = this._MakeHighlightEdgeMaterial(colorInt)
      group.userData.materials = [fillMat, edgeMat]

      // Relleno a ancho completo.
      this._PaintAdvanceLane(group, entity.vertices, origin, totalLength, width, 0, fillMat, 0, null)
      // Borde: dos franjas finas y brillantes en los cantos → remarca la línea.
      const edgeW = Math.max(width * 0.16, 0.08)
      const off   = (width - edgeW) / 2
      this._PaintAdvanceLane(group, entity.vertices, origin, totalLength, edgeW,  off, edgeMat, 1, null)
      this._PaintAdvanceLane(group, entity.vertices, origin, totalLength, edgeW, -off, edgeMat, 1, null)

      // Todo por encima de los carriles de avance (z 0.5, renderOrder 600); el
      // borde brillante por encima del relleno para que remarque el contorno.
      group.traverse((obj) => {
        if (!obj.isMesh) return
        const isEdge = obj.material === edgeMat
        obj.position.z   = isEdge ? 0.82 : 0.80
        obj.renderOrder  = isEdge ? 812  : 800
      })

      scene.add(group)
      this._highlightMeshes[handle] = group
    },

    /** Relleno del resaltado: color chillante y SÓLIDO (sin translucidez) para
     *  que salte a la vista. El borde brillante lo remarca aún más. */
    _MakeHighlightMaterial(colorInt) {
      return new THREE.MeshBasicMaterial({
        color:       colorInt,
        transparent: true,   // se mantiene en el pase transparente (respeta renderOrder vs avance)
        opacity:     1,      // pero totalmente sólido a la vista
        depthTest:   false,
        depthWrite:  false,
        side:        THREE.DoubleSide,
      })
    },

    /** Borde del resaltado: MISMO color que el relleno (línea uniforme, sin rim
     *  aclarado), sólido para remarcar los cantos de la polilínea. */
    _MakeHighlightEdgeMaterial(colorInt) {
      return new THREE.MeshBasicMaterial({
        color:       colorInt,
        transparent: true,
        opacity:     1,
        depthTest:   false,
        depthWrite:  false,
        side:        THREE.DoubleSide,
      })
    },

    /** Aclara un color hex int (Three.js) sumando `amt` a cada canal RGB. */
    _LightenColor(hex, amt) {
      const r = Math.min(255, ((hex >> 16) & 0xff) + amt)
      const g = Math.min(255, ((hex >> 8) & 0xff) + amt)
      const b = Math.min(255, (hex & 0xff) + amt)
      return (r << 16) | (g << 8) | b
    },

    _DisposeAllHighlightMeshes() {
      if (!this._highlightMeshes) return
      const dxf   = this.$refs.viewer?.GetDxfViewer?.()
      const scene = dxf?.GetScene?.()
      for (const h of Object.keys(this._highlightMeshes)) {
        const group = this._highlightMeshes[h]
        if (!group) continue
        if (scene) scene.remove(group)
        group.traverse((obj) => { if (obj.geometry) obj.geometry.dispose?.() })
        for (const m of (group.userData.materials || [])) m?.dispose?.()
        delete this._highlightMeshes[h]
      }
      dxf?.Render?.()
    },

    // ─── Persistencia en localStorage ─────────────────────────────────────
    //
    // Clave por archivo (nombre + tamaño) — no es a prueba de colisiones pero
    // alcanza para esta fase sin backend. Si el usuario sube otro DXF con
    // mismo nombre y tamaño, hidratará el avance previo; suficiente para demo.

    _AdvanceStorageKey() {
      const file = this.uploadedFile
      if (!file?.name) return null
      return ADVANCE_STORAGE_PREFIX + file.name + ':' + (file.size || 0)
    },

    _LoadAdvanceState() {
      const key = this._AdvanceStorageKey()
      if (!key) return {}
      try {
        const raw = localStorage.getItem(key)
        return raw ? JSON.parse(raw) : {}
      } catch (e) {
        logger.warn('No se pudo leer avance del localStorage:', e)
        return {}
      }
    },

    _PersistAdvanceState() {
      const key = this._AdvanceStorageKey()
      if (!key) return
      try {
        // Si el estado quedó vacío, removemos la entrada en lugar de guardar "{}".
        if (Object.keys(this._advanceState).length === 0) {
          localStorage.removeItem(key)
        } else {
          localStorage.setItem(key, JSON.stringify(this._advanceState))
        }
      } catch (e) {
        logger.warn('No se pudo persistir avance:', e)
      }
    },

    /**
     * Re-pinta todos los avances guardados para el archivo actual.
     * Se llama tras onSceneLoaded — necesita el viewer ya inicializado
     * y el cadJson disponible para resolver entity por handle.
     *
     * Orden de hidratación:
     *   1. localStorage (cache rápido por archivo) — pinta de inmediato.
     *   2. Backend (canonical) — Component Mongo del pictograma. Si la
     *      respuesta llega y difiere, reescribe el state y re-pinta.
     *   Backend gana siempre que esté disponible. Si el GET falla, queda
     *   lo que había en localStorage (degrada graciosamente).
     */
    async _RehydrateAdvance() {
      this._advanceState = this._LoadAdvanceState()
      this._PaintAllAdvance()

      // Solo electrical/trenches tienen este endpoint.
      if (!this.pictogram?.id) return
      try {
        // apiCall (helpers/api.helper.js) ya unwrapea axios → retorna response.data directo.
        const data = await getPictogramComponents(this.pictogram.id) || {}
        if (!data.success) return

        const statuses = data.statuses || {}
        const components = Array.isArray(data.components) ? data.components : []

        const next = Object.create(null)
        for (const comp of components) {
          const handle = comp.handle != null ? String(comp.handle) : null
          if (!handle) continue
          const segments = (comp.segments || []).map((seg) => {
            // Mantenemos el mismo shape que produce _OnAdvanceApply:
            //   - `status` es el value (id numérico) — sirve de key para
            //     agrupar en _PaintAdvanceForEntity por status idéntico.
            //   - `statusLabel` / `statusColor` se resuelven contra el
            //     diccionario `statuses` indexado por id.
            const statusObj = statuses[seg.status_id] || null
            return {
              meters:      Number(seg.meters) || 0,
              status:      seg.status_id,
              statusLabel: statusObj ? statusObj.name : null,
              statusColor: statusObj ? statusObj.color : null,
              createdAt:   seg.created_at ? new Date(seg.created_at).getTime() : Date.now(),
              lineId:      seg.line_id ?? null,
              startDate:   seg.start_date ?? null,
            }
          })
          next[handle] = {
            direction: comp.direction || 'l2r',
            // Persistimos totalLength para que tooltips/cálculos no dependan
            // del mesh golpeado. Preferimos el que envía el backend; si no,
            // lo resolvemos desde la entity del cadJson por handle.
            // Redondeado a 2 decimales (precisión de los metros registrados) para
            // que la elegibilidad de estados detecte el 100% exacto (cap == 0).
            totalLength: comp.total_length != null
              ? Math.round(Number(comp.total_length) * 100) / 100
              : Math.round(this._GetEntityTotalLength(this._GetEntityByHandle(handle) || {}) * 100) / 100,
            segments,
          }
        }

        // Limpieza: el primer paint pudo dejar meshes para handles que solo
        // estaban en localStorage. Reseteamos antes de re-pintar desde
        // backend (canonical).
        this._DisposeAllAdvanceMeshes()
        this._advanceState = next
        this._PersistAdvanceState()
        this._PaintAllAdvance()
      } catch (e) {
        logger.warn('Hidratación de avance desde backend falló — uso localStorage:', e)
      }
    },

    /**
     * Pinta todos los handles del _advanceState actual sobre el viewer.
     * Resuelve cada handle a una entity del cadJson y delega en
     * _PaintAdvanceForEntity. Handles sin entity correspondiente se saltan.
     */
    _PaintAllAdvance() {
      const handles = Object.keys(this._advanceState || {})
      if (handles.length === 0) return

      const cadJson = this.processedCadJson || this.cadJson
      if (!cadJson) return
      const raw = cadJson.entities
      const list = !raw ? [] : (Array.isArray(raw) ? raw : Object.values(raw))
      const entityByHandle = Object.create(null)
      for (const e of list) {
        if (e?.handle != null) entityByHandle[String(e.handle)] = e
      }

      for (const handle of handles) {
        const entity = entityByHandle[handle]
        if (!entity) continue
        const state = this._UpgradeAdvanceState(this._advanceState[handle])
        if (!state) continue
        this._advanceState[handle] = state
        this._PaintAdvanceForEntity(handle, entity, state.direction, state.segments)
      }
    },

    // ─── Tooltip al pasar el mouse sobre un avance pintado ────────────────
    //
    // Listener pointermove en el canvas + raycaster propio (no usamos el del
    // overlay porque el del overlay ya consume pointerdown y filtramos por
    // grupo distinto). Hit sobre _advanceMeshes → mostramos floating div.

    _BindHoverTooltip() {
      if (this._hoverHandler) return
      const dxf = this.$refs.viewer?.GetDxfViewer?.()
      const canvas = dxf?.GetCanvas?.()
      if (!canvas) return

      this._hoverRaycaster = new THREE.Raycaster()
      this._hoverPointer   = new THREE.Vector2()
      this._hoverCanvas    = canvas

      this._hoverHandler = (event) => this._OnHoverPointerMove(event, dxf, canvas)
      canvas.addEventListener('pointermove', this._hoverHandler)
      canvas.addEventListener('pointerleave', this._HideTooltip)
    },

    _UnbindHoverTooltip() {
      if (this._hoverCanvas && this._hoverHandler) {
        try {
          this._hoverCanvas.removeEventListener('pointermove', this._hoverHandler)
          this._hoverCanvas.removeEventListener('pointerleave', this._HideTooltip)
        } catch (e) { /* canvas ya removido */ }
      }
      this._hoverCanvas    = null
      this._hoverHandler   = null
      this._hoverRaycaster = null
      this._hoverPointer   = null
      this._HideTooltip()
    },

    _HideTooltip() {
      if (this.advanceTooltip.visible) this.advanceTooltip.visible = false
    },

    _OnHoverPointerMove(event, dxf, canvas) {
      const camera = dxf.GetCamera?.()
      if (!camera) return

      const rect = canvas.getBoundingClientRect()
      this._hoverPointer.x = ((event.clientX - rect.left) / rect.width)  * 2 - 1
      this._hoverPointer.y = -((event.clientY - rect.top)  / rect.height) * 2 + 1
      this._hoverRaycaster.setFromCamera(this._hoverPointer, camera)

      // Buscamos en TRES grupos de meshes para que el tooltip aparezca tanto
      // sobre los carriles pintados COMO sobre la polilínea trackeable (hitbox
      // del overlay) — esta última cubre el caso "polilínea sin avance todavía".
      // Si hay hit sobre cualquiera, recuperamos el handle y mostramos el
      // breakdown completo del estado.
      const targets = []
      // 1. Carriles pintados (siempre tienen userData.handle en su group raíz)
      for (const h of Object.keys(this._advanceMeshes)) {
        const g = this._advanceMeshes[h]
        if (g) targets.push(g)
      }
      // 2. Hitboxes del DxfInteractiveOverlay (cada mesh tiene userData.handle)
      const overlayGroup = this.$refs.refDxfOverlay?.GetGroup?.()
      if (overlayGroup) targets.push(overlayGroup)

      if (targets.length === 0) {
        this._HideTooltip()
        return
      }

      const hits = this._hoverRaycaster.intersectObjects(targets, true)
      if (hits.length === 0) {
        this._HideTooltip()
        return
      }

      // Buscamos el handle subiendo desde el mesh hit. El handle vive en el
      // group raíz (carril) o en el mesh hitbox (overlay).
      let obj = hits[0].object
      while (obj && obj.userData?.handle == null) obj = obj.parent
      const handle = obj?.userData?.handle
      if (!handle) {
        this._HideTooltip()
        return
      }

      const data = this._advanceState[handle]
      // Longitud total: la resolvemos SIEMPRE desde la entity por handle
      // (cadJson), no desde el mesh golpeado. Antes, si el hover caía sobre un
      // carril de avance pintado (que no lleva `entity` en userData) el
      // fallback era {} → 0; solo daba bien si el ray pegaba en un hitbox del
      // overlay. Por eso un carril mostraba "longitud total: 0" y otro no.
      const entity = this._GetEntityByHandle(handle)
        || hits[0].object?.userData?.entity
        || obj.userData?.entity
        || {}
      const totalLength = (data && data.totalLength)
        || this._GetEntityTotalLength(entity)
        || 0

      this.advanceTooltip.visible     = true
      this.advanceTooltip.x           = event.clientX + 12
      this.advanceTooltip.y           = event.clientY + 12
      this.advanceTooltip.totalLength = totalLength

      // Capas CON rechazos (modelo de bolsas): el tooltip muestra la DISTRIBUCIÓN
      // vigente del segmento — por-iniciar (gris), cada etapa con sus metros vigentes
      // (su color) y los rechazados por estado (su color) — COHERENTE con la franja
      // pintada (_CurrentStateSegments) y con el modal (poolBreakdown). Capas SIN
      // rechazos (zanjas: cavar/tapar/fibra) → procesos paralelos: suma por estado.
      const rawSegments = data?.segments || []
      const layer = (this.pictogram?.layers || []).find(l => l.code === entity?.layer)
      const layerStatuses = layer ? this._GetStatusesForLayer(layer) : []
      const hasRejects = layerStatuses.some(s => Number(s.weight) < 0)
      const eps = 1e-6
      let breakdown = []

      if (hasRejects) {
        const pools = this._SimulatePools(rawSegments, layerStatuses, totalLength)
        if ((pools.unstarted || 0) > eps) {
          breakdown.push({ value: '__unstarted', label: this.$t('pending_execution'), color: '#9ca3af', meters: pools.unstarted })
        }
        for (let k = 0; k < pools.M; k++) {
          if ((pools.bags[k] || 0) > eps) {
            const st = layerStatuses[pools.positives[k]]
            breakdown.push({ value: st.value, label: st.label, color: st.color, meters: pools.bags[k] })
          }
        }
        if (pools.rejectedByStatus) {
          for (const [sv, meters] of pools.rejectedByStatus) {
            if ((meters || 0) <= eps) continue
            const rej = layerStatuses.find(s => s.value === sv)
            breakdown.push({ value: 'rej-' + sv, label: rej ? rej.label : this.$t('rejected'), color: (rej && rej.color) || '#e17055', meters })
          }
        }
      } else {
        // Zanjas (sin rechazos): suma cruda por estado (procesos paralelos).
        const map = new Map()
        for (const seg of rawSegments) {
          const key = seg.status
          if (!map.has(key)) {
            map.set(key, { value: seg.status, label: seg.statusLabel || '', color: seg.statusColor || '#9ca3af', meters: 0 })
          }
          map.get(key).meters += seg.meters || 0
        }
        breakdown = [...map.values()]
      }

      this.advanceTooltip.totalMeters = breakdown.reduce((acc, b) => acc + (b.meters || 0), 0)
      this.advanceTooltip.breakdown   = breakdown
    },

    onError(message) {
      logger.error('viewer error:', message)
      this.$toast.open({
        message,
        type: 'is-danger',
        position: 'is-top-right',
      })
    },

    /**
     * _GetLayers retorna los layers que hacen referencia a los layer manejador por nosotros, no a los que estan definidos en el
     * DXF de momento lo harcodeamos, porque son las que por defecto deben crearse y generarse, pero en el futuro hay que retocar
     * esta parte para adicionar otros dinamicamente
     * Propiedad create_entities indica si se van a crear las entidades al momento de generar el parser del DXF, ademas puede usarse
     * para identificar si se inserta en la tabla pictogram_layers (Si es muy ambiguo crear la propiedad adecuada y ajustar)
     * Propiedad in_design indica que viene en el diseño y no hay la necesidad de crear las entidades
     * Propiedad icon es el icono que representara el layer por defecto seria null
     */
     _GetLayers() {
      return getLayerDescriptors({
        totalEntitiesDesign: this.totalEntitiesDesign,
        totalStructures:     this.total_structures,
        createElements:      this.createElements,
      })
    },

    /**
     * Descriptores de capa para electrical/trenches construidos desde la selección
     * hecha en SelectLayersModal. Mismo schema que getLayerDescriptors() para
     * mantener compat con savePictogram y PictogramService::addLayer del backend,
     * más el campo `length` (metros) que se persistirá en pictogram_layers.length.
     *
     * `code` = nombre de la capa DXF original (sirve como identificador único
     * dentro del pictograma). `total_entities` = conteo de entidades de la capa.
     */
    _GetSelectedLayerDescriptors() {
      const selSet = new Set(this.selectedLayerCodes)
      // Convención por tipo de pictograma — todas las capas del mismo
      // pictograma comparten icono y comparten un prefijo numerado de code:
      //   electrical_installation → icon=bolt, code=electrical_1..n
      //   electrical_trenches     → icon=road, code=trench_1..n
      // El prefijo numerado normaliza el `code` (el nombre técnico de la capa
      // DXF puede ser cualquier cosa: "ELECTRICAL_HV_LINE", "Layer_42", etc.)
      // y deja `name` libre para el rename humano del usuario.
      const conventionByType = {
        [PICTOGRAM_TYPES.ELECTRICAL]: { icon: 'bolt', codePrefix: 'electrical' },
        [PICTOGRAM_TYPES.DITCHES]:    { icon: 'road', codePrefix: 'trench'     },
      }
      const conv = conventionByType[this.typePictogram] || { icon: null, codePrefix: 'layer' }

      return this.dxfLayersInfo
        .filter(l => selSet.has(l.name))
        .map((l, i) => ({
          // `code` = identificador normalizado dentro del pictograma (1-based).
          // `name` = lo que verá el usuario; respeta el rename hecho en
          // SelectLayersModal si existe, sino cae al nombre DXF original.
          // `dxf_layer` = nombre técnico de la capa en el DXF (lo usa el
          // encoder binario para mapear entidades a su code normalizado;
          // el backend lo ignora por ahora pero queda en payload por si
          // hace falta el mapping a futuro).
          code:            `${conv.codePrefix}_${i + 1}`,
          name:            this.customLayerNames[l.name] || l.displayName || l.name,
          icon:            conv.icon,
          // Color RGB (0x00RRGGBB) de la capa DXF — el backend lo persiste en
          // pictogram_layers.color y lo replica en el design doc para teñir el
          // icono de capa en la config de BOQ links (igual que mechanical).
          color:           l.color != null ? l.color : null,
          dxf_layer:       l.name,
          order:           i,
          quantity:        l.entityCount,
          total_entities:  l.entityCount,
          // Redondeo a 2 decimales — el backend lo guardará como decimal(14,2).
          length:          Number((l.lengthMeters || 0).toFixed(2)),
          create_entities: true,
          in_design:       true,
        }))
    },

    /**
     * Construye el .epcb binario (Float32 zero-copy) para electrical/trenches
     * y lo sube a S3 vía uppy. Devuelve el `awsResponse` que el caller pasa
     * al `savePictogram` como `sceneAws` — el service lo persiste igual que
     * el JSON del mecánico, pero apuntando al binario.
     *
     * IMPORTANTE: el .epcb incluye TODAS las capas del DXF — es el snapshot
     * visual del diseño completo. La selección del SelectLayersModal tiene
     * otro propósito (filas en `pictogram_layers` para registrar avance);
     * son cosas distintas y deben mantenerse separadas. Si el .epcb tuviera
     * solo las capas seleccionadas, el cliente vería un dibujo recortado.
     *
     * Cada capa va con su nombre DXF original como `code` (no el numerado
     * `electrical_n` que usan los pictogram_layers); eso mantiene el .epcb
     * agnóstico del flow de tracking.
     */
    async _UploadBinaryScene(selectedDescriptors = []) {
      // CRÍTICO: NO usar `this.dxfLayersInfo` para construir el .epcb.
      //
      // `dxfLayersInfo` se arma con un filtro adicional (getTextLayers) que
      // descarta cualquier capa con TEXT/MTEXT o INSERTs a blocks con texto.
      // Ese filtro tiene sentido para el modal de selección (no se ofrece
      // "capa de etiquetas" como trackeable) pero NO para el render: una capa
      // con polilíneas + 1 etiqueta de texto debe igual ir al .epcb con sus
      // polilíneas. El encoder a nivel de entidad ya descarta los TEXT/MTEXT.
      //
      // Por eso consultamos el viewer directamente con GetLayers(true) — todas
      // las capas no-vacías (al menos una entidad renderizada), sin filtro.
      const viewerLayers = this.$refs.viewer?.GetLayers?.(true) || []
      if (viewerLayers.length === 0) {
        throw new Error('layers_not_built')
      }

      // Mapping `dxf_layer → pictogram_layer.code` para las capas seleccionadas.
      // Esto permite que en el .epcb las polilíneas trackeables vayan con el
      // code numerado (electrical_n / trench_n) — el mismo que se persiste en
      // pictogram_layers — así el cliente puede cruzarlas en O(1) con un
      // `pictogram.layers.find(l => l.code === entity.layer)`.
      //
      // Las capas NO seleccionadas siguen con el nombre DXF original: van al
      // .epcb como contexto visual del diseño completo, pero no son
      // clickeables para registrar avance (no matchean con pictogram_layers).
      const codeByDxfLayer = Object.create(null)
      for (const d of selectedDescriptors) {
        if (d.dxf_layer) codeByDxfLayer[d.dxf_layer] = d.code
      }

      const encoderLayers = viewerLayers.map(l => ({
        dxfLayer: l.name,
        code:     codeByDxfLayer[l.name] || l.name,
        color:    l.color,
      }))

      const buf = encodeScene(this.cadJson, { layers: encoderLayers })
      logger.info(
        'binary scene encoded:', buf.byteLength, 'bytes /',
        encoderLayers.length, 'viewer layers (',
        selectedDescriptors.length, 'trackable)'
      )

      // Nombre paralelo al DXF original (cambiamos la extensión a .epcb).
      const baseName = (this.uploadedFile?.name || 'pictogram').replace(/\.[^.]+$/, '')
      const file = new File([buf], baseName + '.epcb', {
        type: 'application/octet-stream',
      })
      return await uploadFile(file)
    },

    async _OnConfigureSubdivisionsApply({ cadLayeredDesign, createElements }) {
      this.cadLayeredDesign = cadLayeredDesign
      this.createElements   = createElements

      // Fase 1 — "Preparando procesador..."
      this.ShowLoader({ loading: true, text: this.$t('preparing_processor') })

      // Estado para calcular ETA durante el progreso
      const startTime = performance.now()
      // Umbral por encima del cual se muestra el aviso "no cierres la página"
      const WARN_THRESHOLD = 500

      try {
        const { totalEntitiesDesign, processedCadJson } = await processSubdivisions({
          cadJson:          this.cadJson,
          layers:           this._GetLayers(),
          cadLayeredDesign,
          onProgress: ({ processed, total, percent }) => {
            // Fase 2 — "Procesando subdivisiones X/Y (Z%)" + ETA + warning condicional
            const lines = [this.$t('processing_subdivisions_progress', [processed, total, percent])]
            // ETA: a partir del 5% el cálculo se estabiliza
            if (percent >= 5 && percent < 100) {
              const elapsedMs = performance.now() - startTime
              const remainingMs = (elapsedMs / processed) * (total - processed)
              lines[0] += ' · ' + this.$t('eta_seconds', [Math.round(remainingMs / 1000)])
            }
            // Aviso para procesos largos
            if (total > WARN_THRESHOLD) {
              lines.push(this.$t('do_not_close_page'))
            }
            this.ShowLoader({ loading: true, text: lines.join('\n') })
          },
        })

        // Fase 3 — "Construyendo escena 3D..."
        // Damos 2 frames al browser para que pinte este texto ANTES de asignar
        // processedCadJson (que dispara el watcher del Viewer y DxfScene.Build,
        // que sí bloquea el main thread por varios segundos).
        this.ShowLoader({ loading: true, text: this.$t('building_scene_3d') })
        await this.$nextTick()
        await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)))

        this.totalEntitiesDesign = totalEntitiesDesign
        this.processedCadJson    = processedCadJson
        // La vista por defecto se aplica en `onSceneLoaded` (evento epc-loaded del Viewer),
        // antes del primer paint, para evitar el flash de "todas las layers visibles".
      } catch (err) {
        this.$toast.open({
          message:  err.message || this.$t('error_processing'),
          type:     'is-danger',
          position: 'is-top-right',
        })
      } finally {
        this.ShowLoader({ loading: false, text: null })
      }
    },

    async _OnSavePictogramApply({ name, code, type }) {
      try {
        // El modal no propaga el tipo: el componente lo conoce por prop.
        // Sin esto el service caería al default MECHANICAL y dispararía
        // el worker de scene (errores tipo "blockCtx is not defined").
        const effectiveType = type || this.typePictogram
        const isMechanical = effectiveType === PICTOGRAM_TYPES.MECHANICAL

        // Validación específica para electrical/trenches: el usuario debe haber
        // marcado al menos una capa en el footer. Sin capas no hay nada que
        // registrar en pictogram_layers y el guardado pierde sentido.
        const layersPayload = isMechanical
          ? this._GetLayers()
          : this._GetSelectedLayerDescriptors()
        if (!isMechanical && layersPayload.length === 0) {
          this.$toast.open({
            message:  this.$t('select_at_least_one_layer'),
            type:     'is-warning',
            position: 'is-top-right',
          })
          return
        }

        // Para electrical/trenches generamos el scene binario .epcb y lo
        // subimos antes del POST al pictograma. El service `savePictogram`
        // ya está preparado para aceptar `sceneAws` precocido y propagarlo
        // al backend en `aws_response`.
        let sceneAws = null
        if (!isMechanical) {
          this.ShowLoader({ loading: true, text: this.$t('uploading_scene') })
          // Pasamos los descriptors de capas seleccionadas para que el
          // encoder mapee sus polilíneas con el code numerado del
          // pictogram_layer (electrical_n / trench_n). Las no seleccionadas
          // siguen con el nombre DXF original como code en el .epcb.
          sceneAws = await this._UploadBinaryScene(layersPayload)
          if (!sceneAws) throw new Error('upload_scene_error')
        }

        // Capturas:
        //   - mechanical_installation: una por cada vista (pilings/structures/…)
        //     porque el viewer-konva las usa para previsualizar progreso por capa.
        //   - electrical/trenches: una sola captura general que sirve como
        //     preview del pictograma en listados (no hay vistas configurables).
        this.ShowLoader({ loading: true, text: this.$t('generating_screenshots') })
        this.showPanelScreenshot = true
        let screenshots = []
        if (isMechanical) {
          screenshots = await captureViewerByViews({
            viewer:     this.$refs.viewer,
            views:      this.viewsList,
            applyView:  (c) => this._ApplyDisplayView(c),
            onPreview:  (url) => { this.screenshot = url },
          })
          // Restaurar vista por defecto tras capturar.
          this._ApplyDisplayView(this.displayView)
        } else {
          // Derivamos el nombre del archivo desde el tipo del pictograma para
          // que la table de pictogramas pueda buscarlo de forma consistente
          // (mecánico usa nombres como `pilings.png`/`structures.png` derivados
          // del code; aquí usamos `electrical.png` / `trench.png`).
          const screenshotNameByType = {
            [PICTOGRAM_TYPES.ELECTRICAL]: 'electrical.png',
            [PICTOGRAM_TYPES.DITCHES]:    'trench.png',
          }
          screenshots = await captureViewerSnapshot({
            viewer:    this.$refs.viewer,
            onPreview: (url) => { this.screenshot = url },
            name:      screenshotNameByType[effectiveType] || 'preview.png',
          })
        }

        const pictogram = await savePictogram({
          entityId:         this.entityId,
          dxfFile:          this.uploadedFile,
          // Si el usuario procesó subdivisiones usamos el cadJson procesado;
          // si no, fallback al cadJson original del parser. Ambos son válidos
          // para el flow de guardado (el service decide si lo pasa al worker
          // según el tipo).
          cadJson:          this.processedCadJson || this.cadJson,
          screenshots,
          name, code,
          type:             effectiveType,
          layers:           layersPayload,
          cadLayeredDesign: this.cadLayeredDesign,
          // electrical/trenches: pasamos el .epcb ya subido para que el
          // service no llame al worker de mecánico. Mech queda en null y el
          // service usa CadWorkerClient como siempre.
          sceneAws,
          onProgress:       (key) => this.ShowLoader({ loading: true, text: this.$t(key) }),
        })

        // Resumen para el modal de éxito (mismos números que vienen del worker).
        this.$refs.refSaveSuccessModal.show({
          dxfFileName: this.uploadedFile?.name ?? null,
          structures:  this.total_structures,
          pilings:     this.totalEntitiesDesign?.pilings,
          modules:     this.totalEntitiesDesign?.modules,
          panels:      this.totalEntitiesDesign?.panels,
          screenshots: screenshots.length,
        })
        this.$emit('epc-saved', pictogram)
      } catch (err) {
        logger.error('save pictogram error:', err)
        this.$toast.open({
          message:  this.$t(err.message) || err.message || this.$t('save_error'),
          type:     'is-danger',
          position: 'is-top-right',
        })
      } finally {
        this.showPanelScreenshot = false
        this.ShowLoader({ loading: false, text: null })
      }
    },
  },

  created() {
    this.fonts = FONTS
    // Propiedades de instancia (NO reactivas) para el avance lineal.
    // Mantienen referencias a estructuras pesadas (THREE.Group, cad entity con
    // arrays de vertices) sin que Vue las observe.
    this._advanceMeshes  = Object.create(null) // handle → THREE.Group
    this._highlightMeshes = Object.create(null) // handle → THREE.Group (resaltado footer)
    this._advanceCurrent = null                 // { handle, entity } activo en el modal
    // Estado persistido cargado desde localStorage: handle (string) → { direction, meters, totalLength, status }
    this._advanceState   = Object.create(null)
    // Tooltip — recursos del raycaster propio de hover, instanciados una sola vez.
    this._hoverRaycaster = null
    this._hoverPointer   = null
    this._hoverCanvas    = null
    this._hoverHandler   = null
  },

  beforeDestroy() {
    this._UnbindHoverTooltip()
    this._DisposeAllAdvanceMeshes()
    this._DisposeAllHighlightMeshes()
  },
}
</script>

<style scoped>

/* Evita que la suma de header + viewer + Toolbar exceda el viewport
   (el shell de la app ocupa ~6rem arriba) y genere scroll global. */
.relative.w-full {
  max-height: calc(100vh - 6rem);
  overflow: hidden;
}

/* Dropzone a pantalla completa del body cuando aún no hay archivo cargado.
   Buefy renderiza b-upload como un wrapper inline; con .upload-full pasa
   a bloque que llena el contenedor flex, y el área draggable interna
   (.upload-draggable) se expande y centra el contenido. */
.upload-full,
.upload-full :deep(.upload-draggable) {
  width: 100%;
  height: 100%;
}

.upload-full :deep(.upload-draggable) {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Tooltip flotante de avance lineal — posicionado con coords del mouse. */
.advance-tooltip {
  position: fixed;
  background: rgba(33, 33, 33, 0.92);
  color: #fff;
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 12px;
  pointer-events: none;
  z-index: 9999;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
}

.advance-tooltip__title {
  font-weight: 600;
  margin-bottom: 2px;
}

/* Indicador flotante de capa activa — pill centrado arriba del viewer. */
.active-layer-pill {
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 6;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 70%;
  padding: 6px 12px;
  border-radius: 9999px;
  background: rgba(34, 34, 34, 0.92);
  color: #f4f4f4;
  font-size: 13px;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
  transition: background-color 0.15s ease;
  user-select: none;
}

.active-layer-pill:hover {
  background: rgba(0, 0, 0, 0.96);
  color: #00bfff;
}

.active-layer-pill__text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.active-layer-pill strong {
  color: #00bfff;
  font-weight: 600;
}

</style>
